<template>
  <div class="accts-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="Accounts" />
      <main class="page-content">
        <div class="accts-page">

          <!-- PAGE HEADER -->
          <div class="page-header">
            <div class="page-header-left">
              <div class="page-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
              </div>
              <div>
                <h1 class="page-title">Accounts</h1>
                <p class="page-sub">Manage all platform accounts and subscriptions</p>
              </div>
            </div>
            <div class="header-actions">
              <button class="btn-secondary" @click="waShowBillingConfig = true">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6zm0 4h8v2H6zm10 0h2v2h-2zm-6-4h8v2h-8z"/></svg>
                WA Billing Config
              </button>
              <button class="btn-secondary" @click="openBulk">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
                Add Multiple Users
              </button>
              <button class="btn-primary" @click="openNew">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                New Account
              </button>
            </div>
          </div>

          <!-- LOADING / ERROR -->
          <div v-if="pageLoading" class="page-loading">
            <span class="spinner spinner--lg"></span>
            <span>Loading accounts…</span>
          </div>
          <div v-else-if="pageError" class="page-error">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            {{ pageError }}
            <button class="btn-retry" @click="loadData">Retry</button>
          </div>

          <template v-else>

          <!-- STAT CHIPS -->
          <div class="stat-chips">
            <div class="chip chip--gray">
              <span class="chip-n">{{ stats.total }}</span>
              <span class="chip-l">Total</span>
            </div>
            <div class="chip chip--green">
              <span class="chip-n">{{ stats.active }}</span>
              <span class="chip-l">Active</span>
            </div>
            <div class="chip chip--blue">
              <span class="chip-n">{{ stats.pending }}</span>
              <span class="chip-l">Pending</span>
            </div>
            <div class="chip chip--orange">
              <span class="chip-n">{{ stats.expiringSoon }}</span>
              <span class="chip-l">Expiring Soon</span>
            </div>
            <div class="chip chip--red">
              <span class="chip-n">{{ stats.expired }}</span>
              <span class="chip-l">Expired</span>
            </div>
          </div>

          <!-- TOOLBAR -->
          <div class="toolbar">
            <div class="search-box">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input v-model="search" placeholder="Search by name, company, email or phone…" />
            </div>
            <select v-model="filterStatus" class="filter-select">
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Expired">Expired</option>
              <option value="expiring">Expiring ≤10 days</option>
              <option value="recently-expired">Expired ≤10 days ago</option>
            </select>
            <select v-model="filterPlan" class="filter-select">
              <option value="">All Plans</option>
              <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <span class="result-count">{{ filteredAccounts.length }} account{{ filteredAccounts.length !== 1 ? 's' : '' }}</span>
          </div>

          <!-- TABLE -->
          <div class="table-card">
            <table class="accts-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Credit Line Status</th>
                  <th>CL Credit</th>
                  <th>Quali-D Credits</th>
                  <th>Expires</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="acc in filteredAccounts"
                  :key="acc.id"
                  class="acct-row"
                  @click="openDetail(acc)"
                >
                  <td>
                    <div class="acct-cell">
                      <div class="avatar" :style="{ background: avatarColor(acc.name) }">{{ initials(acc.name) }}</div>
                      <div class="acct-info">
                        <div class="acct-name">{{ acc.name }}</div>
                        <div class="acct-company">{{ acc.company_name }}</div>
                        <div class="acct-email">{{ acc.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="plan-cell">
                    <span class="plan-badge">{{ acc.plan_name || '—' }}</span>
                    <button class="btn-plan-change" @click.stop="openChangePlan(acc)">Change</button>
                  </td>
                  <td>
                    <span :class="['status-badge', 'status-' + acc.status.toLowerCase()]">{{ acc.status }}</span>
                  </td>
                  <td class="wa-billing-cell">
                    <span v-if="waBillingMap[acc.id] && waBillingMap[acc.id].credit_line_active" class="wa-badge wa-badge--active">✓ Linked</span>
                    <span v-else class="wa-badge wa-badge--none">✗ Not linked</span>
                  </td>
                  <td class="wa-billing-cell">
                    <template v-if="waBillingMap[acc.id]">
                      <div class="wa-balance">${{ Number(waBillingMap[acc.id].usd_remaining).toFixed(2) }}</div>
                    </template>
                    <span v-else class="wa-text-muted">—</span>
                  </td>
                  <td class="credit-cell">
                    <span :class="['credit-badge', acc.credit > 0 ? 'credit-badge--has' : 'credit-badge--zero']">
                      {{ (acc.credit || 0).toLocaleString() }}
                    </span>
                    <button class="btn-credit-quick" @click.stop="openQuickCredit(acc)">+ Add</button>
                  </td>
                  <td class="date-cell">
                    <span v-if="acc.expires" :class="{ 'text-orange': isExpiringSoon(acc.expires), 'text-red': isExpired(acc.expires) }">
                      {{ acc.expires }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="date-cell">{{ formatDate(acc.created_at) }}</td>
                  <td>
                    <button class="btn-view" @click.stop="openDetail(acc)">
                      View
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredAccounts.length === 0">
                  <td colspan="8" class="empty-row">No accounts match your filters.</td>
                </tr>
              </tbody>
            </table>
          </div>

          </template>

        </div>
      </main>
    </div>

    <!-- OVERLAY -->
    <transition name="fade">
      <div v-if="drawerOpen" class="drawer-overlay" @click="closeDrawer"></div>
    </transition>

    <!-- ═══ NEW ACCOUNT DRAWER ═══ -->
    <transition name="drawer">
      <div v-if="drawerOpen && drawerMode === 'new'" class="drawer">
        <div class="drawer-header">
          <h2 class="drawer-title">New Account</h2>
          <button class="drawer-close" @click="closeDrawer">&times;</button>
        </div>

        <div class="drawer-body">
          <div class="form-group">
            <label>Full Name <span class="req">*</span></label>
            <input v-model="newForm.name" type="text" placeholder="e.g. Karim Mansour" />
          </div>

          <div class="form-group">
            <label>Company Name <span class="req">*</span></label>
            <input v-model="newForm.company_name" type="text" placeholder="e.g. Acme Corp" />
          </div>

          <div class="form-group">
            <label>Phone <span class="req">*</span></label>
            <div class="phone-row">
              <select v-model="newForm.country_code" class="country-select" @change="newFormPhoneError = ''">
                <option v-for="c in countryCodes" :key="c.code" :value="c.code">+{{ c.code }}</option>
              </select>
              <input v-model="newForm.phone" type="text" placeholder="1012345678" @input="newFormPhoneError = ''" @blur="checkNewFormPhone" />
            </div>
            <p v-if="newFormPhoneError" class="field-hint field-hint--error">{{ newFormPhoneError }}</p>
          </div>

          <div class="form-group">
            <label>Email <span class="req">*</span></label>
            <input v-model="newForm.email" type="email" placeholder="user@company.com" />
          </div>

          <div class="form-group">
            <label>Password <span class="req">*</span></label>
            <div class="password-row">
              <input v-model="newForm.password" type="text" class="password-input" readonly />
              <button class="btn-icon" title="Generate new password" @click="regeneratePassword">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4a8 8 0 0 0-8 8 8 8 0 0 0 8 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18a6 6 0 0 1-6-6 6 6 0 0 1 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
              </button>
              <button class="btn-icon" :class="{ 'btn-icon--done': passwordCopied }" title="Copy password" @click="copyPassword">
                <svg v-if="!passwordCopied" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
                <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </button>
            </div>
            <p class="field-hint">Auto-generated — copy before closing.</p>
          </div>

          <div class="form-group">
            <label>Plan <span class="req">*</span></label>
            <div class="plan-mode-toggle">
              <button
                :class="['toggle-btn', newForm.planMode === 'standard' ? 'toggle-btn--on' : '']"
                @click="newForm.planMode = 'standard'"
              >Standard</button>
              <button
                :class="['toggle-btn', newForm.planMode === 'enterprise' ? 'toggle-btn--enterprise' : '']"
                @click="newForm.planMode = 'enterprise'"
              >Enterprise</button>
            </div>

            <template v-if="newForm.planMode === 'standard'">
              <select v-model="newForm.plan_id" class="full-select">
                <option value="">Select a plan…</option>
                <option v-for="p in plans" :key="p.id" :value="p.id">
                  {{ p.name }} — {{ p.price.toLocaleString() }} EGP
                </option>
              </select>
            </template>

            <template v-else>
              <div class="enterprise-box">
                <div class="form-row-2">
                  <div class="form-group-sm">
                    <label>Label</label>
                    <input v-model="newForm.enterprise.name" type="text" placeholder="Enterprise" />
                  </div>
                  <div class="form-group-sm">
                    <label>Price (EGP) *</label>
                    <input v-model="newForm.enterprise.price" type="number" placeholder="0" />
                  </div>
                </div>
                <div class="form-row-2">
                  <div class="form-group-sm">
                    <label>Billing Cycle</label>
                    <select v-model="newForm.enterprise.plan_type">
                      <option value="monthly">Monthly (30 days)</option>
                      <option value="annual">Annual (365 days)</option>
                    </select>
                  </div>
                  <div class="form-group-sm">
                    <label>Agents Limit *</label>
                    <input v-model="newForm.enterprise.agents_limit" type="number" placeholder="10" />
                  </div>
                </div>
                <div class="form-group-sm">
                  <label>Credits *</label>
                  <input v-model="newForm.enterprise.credit_amount" type="number" placeholder="500" />
                </div>
              </div>
            </template>
          </div>

          <div class="form-group">
            <label>Subscription Start Date</label>
            <input v-model="newForm.from_date" type="date" />
            <p class="field-hint">Subscription runs from this date for the plan duration.</p>
          </div>

          <div class="payment-box" :class="{ 'payment-box--disabled': !newFormPlanSelected }">
            <label class="checkbox-label">
              <input type="checkbox" v-model="newForm.paid" :disabled="!newFormPlanSelected" />
              <span>Payment already received (bank transfer / cash)</span>
            </label>
            <p v-if="!newFormPlanSelected" class="field-hint" style="margin-top:6px">Select a plan first to record a payment.</p>
            <div v-if="newForm.paid" class="form-row-2 mt-10">
              <div class="form-group-sm">
                <label>Amount (EGP) *</label>
                <input
                  v-model="newForm.amount"
                  type="number"
                  placeholder="0.00"
                  :readonly="newForm.planMode === 'standard' && !!newForm.plan_id"
                  :class="{ 'input-locked': newForm.planMode === 'standard' && !!newForm.plan_id }"
                />
                <p v-if="newForm.planMode === 'standard' && !!newForm.plan_id" class="field-hint">Fixed to plan price.</p>
              </div>
              <div class="form-group-sm">
                <label>Notes</label>
                <input v-model="newForm.notes" type="text" placeholder="Bank ref, etc." />
              </div>
            </div>
          </div>

          <div v-if="newFormError" class="form-error">{{ newFormError }}</div>
        </div>

        <div class="drawer-footer">
          <button class="btn-cancel" @click="closeDrawer">Cancel</button>
          <button class="btn-submit" :disabled="newFormLoading" @click="submitNew">
            <span v-if="newFormLoading" class="spinner"></span>
            {{ newFormLoading ? 'Creating…' : 'Create Account' }}
          </button>
        </div>
      </div>
    </transition>

    <!-- ═══ ACCOUNT DETAIL DRAWER ═══ -->
    <transition name="drawer">
      <div v-if="drawerOpen && drawerMode === 'detail' && selectedAccount" class="drawer drawer--wide">
        <div class="drawer-header">
          <div class="drawer-header-info">
            <div class="drawer-avatar" :style="{ background: avatarColor(selectedAccount.name) }">
              {{ initials(selectedAccount.name) }}
            </div>
            <div>
              <h2 class="drawer-title">{{ selectedAccount.company_name }}</h2>
              <p class="drawer-sub">{{ selectedAccount.name }} &middot; {{ selectedAccount.email }}</p>
            </div>
          </div>
          <button class="drawer-close" @click="closeDrawer">&times;</button>
        </div>

        <!-- Status bar -->
        <div class="detail-status-bar">
          <span :class="['status-badge', 'status-' + selectedAccount.status.toLowerCase()]">
            {{ selectedAccount.status }}
          </span>
          <span class="detail-plan">{{ selectedAccount.plan_name }}</span>
          <span v-if="selectedAccount.expires" class="detail-expires">
            Expires {{ selectedAccount.expires }}
          </span>
          <span v-else class="text-muted">No subscription date</span>
        </div>

        <!-- Tabs -->
        <div class="detail-tabs">
          <button
            v-for="t in detailTabs"
            :key="t.id"
            :class="['detail-tab', activeTab === t.id ? 'detail-tab--active' : '']"
            @click="activeTab = t.id"
          >{{ t.label }}</button>
        </div>

        <div class="drawer-body">

          <!-- ── ACCOUNT TAB ── -->
          <div v-if="activeTab === 'account'">
            <div class="form-group">
              <label>Full Name</label>
              <input v-model="editForm.name" type="text" />
            </div>
            <div class="form-group">
              <label>Company Name</label>
              <input v-model="editForm.company_name" type="text" />
            </div>
            <div class="form-group">
              <label>Email</label>
              <input v-model="editForm.email" type="email" />
            </div>
            <div class="form-group">
              <label>Phone</label>
              <div class="phone-row">
                <span class="phone-code-static">+{{ selectedAccount.country_code }}</span>
                <input v-model="editForm.phone" type="text" />
              </div>
            </div>
            <div class="form-group">
              <label>Reset Password</label>
              <div class="password-row">
                <input
                  :value="editForm.newPassword || ''"
                  type="text"
                  class="password-input"
                  readonly
                  placeholder="Click ↻ to generate a new password"
                />
                <button class="btn-icon" @click="regenerateEditPassword">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4a8 8 0 0 0-8 8 8 8 0 0 0 8 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18a6 6 0 0 1-6-6 6 6 0 0 1 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                </button>
              </div>
              <p v-if="editForm.newPassword" class="field-hint field-hint--warn">
                New password set — will apply when you save.
              </p>
            </div>
            <button class="btn-submit btn-full mt-16" @click="saveAccount">Save Changes</button>
          </div>

          <!-- ── COMPANY TAB ── -->
          <div v-if="activeTab === 'company'">
            <div class="form-group">
              <label>Address</label>
              <input v-model="editForm.address" type="text" placeholder="Street, City" />
            </div>
            <div class="form-group">
              <label>Business Phone</label>
              <input v-model="editForm.company_phone" type="text" placeholder="020XXXXXXXX" />
            </div>
            <div class="form-group">
              <label>Working Hours</label>
              <div class="time-row">
                <input v-model="editForm.work_hours_from" type="time" />
                <span class="time-sep">to</span>
                <input v-model="editForm.work_hours_to" type="time" />
              </div>
            </div>
            <div class="form-group">
              <label>Working Days</label>
              <div class="days-row">
                <label v-for="d in weekDays" :key="d.val" class="day-check">
                  <input type="checkbox" :value="d.val" v-model="editForm.work_days" />
                  <span>{{ d.label }}</span>
                </label>
              </div>
            </div>
            <div class="form-group">
              <label>Google Maps URL</label>
              <input v-model="editForm.location_url" type="url" placeholder="https://maps.google.com/…" />
            </div>
            <div class="form-group">
              <label>Notes</label>
              <textarea v-model="editForm.notes" rows="3" placeholder="Any additional info…"></textarea>
            </div>
            <button class="btn-submit btn-full mt-16" @click="saveCompany">Save Company Profile</button>
          </div>

          <!-- ── PAYMENTS TAB ── -->
          <div v-if="activeTab === 'payments'">
            <div class="payments-header">
              <h3 class="tab-section-title">Payment History</h3>
              <button class="btn-secondary-sm" @click="showAddPayment = !showAddPayment">
                {{ showAddPayment ? 'Cancel' : '+ Add Payment' }}
              </button>
            </div>

            <!-- Add Payment form -->
            <div v-if="showAddPayment" class="add-payment-form">
              <div class="form-group">
                <label>Plan</label>
                <select v-model="addPaymentForm.plan_id" class="full-select">
                  <option value="">Keep current ({{ selectedAccount.plan_name }})</option>
                  <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
              <div class="form-row-2">
                <div class="form-group-sm">
                  <label>Amount (EGP) *</label>
                  <input v-model="addPaymentForm.amount" type="number" placeholder="0" />
                </div>
                <div class="form-group-sm">
                  <label>From Date</label>
                  <input v-model="addPaymentForm.from_date" type="date" />
                </div>
              </div>
              <div class="form-group">
                <label>Notes</label>
                <input v-model="addPaymentForm.notes" type="text" placeholder="Bank ref, etc." />
              </div>
              <button class="btn-submit btn-full" @click="savePayment">Save Payment</button>
            </div>

            <div v-if="selectedAccount.payments.length === 0 && !showAddPayment" class="empty-state">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="opacity:0.3">
                <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
              </svg>
              <p>No payment records yet.</p>
            </div>

            <div v-else class="payment-list">
              <div v-for="p in selectedAccount.payments" :key="p.id" class="payment-item">
                <div class="payment-left">
                  <div class="payment-ref">{{ p.ref }}</div>
                  <div class="payment-meta">{{ p.plan }} &middot; {{ p.method }}</div>
                </div>
                <div class="payment-right">
                  <div class="payment-amount">{{ p.amount.toLocaleString() }} EGP</div>
                  <div class="payment-date">{{ p.date }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── CREDITS TAB ── -->
          <div v-if="activeTab === 'credits'">
            <div class="credit-balance-box">
              <div class="credit-balance-label">Current Balance</div>
              <div class="credit-balance-val">{{ (selectedAccount.credit || 0).toLocaleString() }}<span class="credit-unit">cr</span></div>
            </div>

            <div class="form-group">
              <label>Add Credits</label>
              <div class="credit-presets">
                <button
                  v-for="p in creditPresets"
                  :key="p"
                  :class="['credit-preset-btn', addCreditAmount === p ? 'credit-preset-btn--active' : '']"
                  @click="addCreditAmount = p"
                >{{ p.toLocaleString() }}</button>
              </div>
              <input v-model.number="addCreditAmount" type="number" min="1" placeholder="Or type a custom amount…" class="mt-10" />
            </div>

            <button
              class="btn-submit btn-full mt-16"
              :disabled="!addCreditAmount || addCreditLoading"
              @click="addCredit"
            >
              <span v-if="addCreditLoading" class="spinner"></span>
              {{ addCreditLoading ? 'Adding…' : 'Add ' + (addCreditAmount ? Number(addCreditAmount).toLocaleString() : 0) + ' Credits' }}
            </button>
          </div>

          <!-- ── WA BILLING TAB ── -->
          <div v-if="activeTab === 'wa_billing'">

            <div v-if="waBillingDetailLoading" class="page-loading">
              <span class="spinner spinner--lg"></span>
              <span>Loading…</span>
            </div>

            <template v-if="waBillingDetail">

              <!-- Credit Mode Card -->
              <div class="wa-section-card">
                <div class="wa-section-header">
                  <h4 class="tab-section-title">Credit Mode</h4>
                  <span v-if="waBillingDetail.allocation && waBillingDetail.allocation.status === 'active'" class="wa-badge wa-badge--active">Active</span>
                  <span v-else-if="waBillingDetail.allocation && waBillingDetail.allocation.status === 'revoked'" class="wa-badge wa-badge--revoked">Disabled</span>
                  <span v-else class="wa-badge wa-badge--none">Inactive</span>
                </div>

                <div v-if="waBillingDetail.allocation">
                  <div class="wa-meta-row">
                    <span class="wa-meta-label">Mode</span>
                    <span class="wa-meta-val">{{ waBillingDetail.allocation.allocation_config_id ? 'Meta Credit Line' : 'Manual (Prepaid)' }}</span>
                  </div>
                  <div v-if="waBillingDetail.allocation.allocation_config_id" class="wa-meta-row">
                    <span class="wa-meta-label">Allocation ID</span>
                    <span class="wa-meta-val">{{ waBillingDetail.allocation.allocation_config_id }}</span>
                  </div>
                  <div class="wa-meta-row">
                    <span class="wa-meta-label">Currency</span>
                    <span class="wa-meta-val">{{ waBillingDetail.allocation.waba_currency }}</span>
                  </div>
                  <div class="wa-meta-row" v-if="waBillingDetail.allocation.shared_at">
                    <span class="wa-meta-label">Enabled</span>
                    <span class="wa-meta-val">{{ waBillingDetail.allocation.shared_at.slice(0, 10) }}</span>
                  </div>
                  <div class="wa-meta-row" v-if="waBillingDetail.allocation.revoked_at">
                    <span class="wa-meta-label">Disabled</span>
                    <span class="wa-meta-val">{{ waBillingDetail.allocation.revoked_at.slice(0, 10) }}
                      <span v-if="waBillingDetail.allocation.revoke_reason" class="text-muted"> — {{ waBillingDetail.allocation.revoke_reason }}</span>
                    </span>
                  </div>
                </div>
                <div v-else class="wa-notice">
                  When enabled, each message sent by this user will deduct from their prepaid balance. Messages are blocked when balance reaches $0.
                </div>

                <div class="wa-action-row">
                  <!-- Enable Credit Mode (manual — works today) -->
                  <button
                    v-if="!waBillingDetail.allocation || waBillingDetail.allocation.status !== 'active'"
                    class="btn-submit"
                    :disabled="waEnableLoading"
                    @click="enableCreditMode"
                  >
                    <span v-if="waEnableLoading" class="spinner"></span>
                    {{ waEnableLoading ? 'Enabling…' : 'Enable Credit Mode' }}
                  </button>
                  <!-- Share via Meta (BSP / Solution Partner only — disabled until upgrade) -->
                  <button
                    v-if="!waBillingDetail.allocation || waBillingDetail.allocation.status !== 'active'"
                    class="btn-secondary-sm"
                    :disabled="true"
                    style="opacity:.4;cursor:not-allowed;pointer-events:none;"
                    title="Requires Meta Solution Partner status — upgrade pending"
                  >
                    Share via Meta (BSP)
                  </button>
                  <!-- Disable -->
                  <button
                    v-if="waBillingDetail.allocation && waBillingDetail.allocation.status === 'active'"
                    class="btn-danger-outline"
                    :disabled="waRevokeLoading"
                    @click="revokeCreditLine"
                  >
                    <span v-if="waRevokeLoading" class="spinner"></span>
                    {{ waRevokeLoading ? 'Disabling…' : (waBillingDetail.allocation.allocation_config_id ? 'Revoke Meta Credit Line' : 'Disable Credit Mode') }}
                  </button>
                </div>
              </div>

              <!-- Balance Summary Card -->
              <div class="wa-section-card">
                <div class="wa-section-header">
                  <h4 class="tab-section-title">Prepaid Balance</h4>
                  <button class="btn-secondary-sm" @click="waShowTopupForm = !waShowTopupForm">
                    {{ waShowTopupForm ? 'Cancel' : '+ Add Top-up' }}
                  </button>
                </div>

                <div class="wa-balance-grid">
                  <div class="wa-balance-item">
                    <div class="wa-bal-label">Received</div>
                    <div class="wa-bal-val">{{ waBillingDetail.balance.total_egp_received.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} EGP</div>
                  </div>
                  <div class="wa-balance-item">
                    <div class="wa-bal-label">Credited</div>
                    <div class="wa-bal-val">${{ waBillingDetail.balance.total_usd_credited.toFixed(2) }}</div>
                  </div>
                  <div class="wa-balance-item">
                    <div class="wa-bal-label">Consumed</div>
                    <div class="wa-bal-val">${{ waBillingDetail.balance.total_usd_consumed.toFixed(2) }}</div>
                  </div>
                  <div class="wa-balance-item wa-balance-item--highlight">
                    <div class="wa-bal-label">Remaining</div>
                    <div class="wa-bal-val">${{ waBillingDetail.balance.total_usd_remaining.toFixed(2) }}</div>
                  </div>
                </div>

                <!-- Top-up form -->
                <div v-if="waShowTopupForm" class="wa-topup-form">

                  <!-- Currency toggle -->
                  <div class="wa-currency-toggle">
                    <button :class="['wa-cur-btn', waTopupForm.currency === 'EGP' ? 'wa-cur-btn--active' : '']" @click="waTopupForm.currency = 'EGP'">EGP Payment</button>
                    <button :class="['wa-cur-btn', waTopupForm.currency === 'USD' ? 'wa-cur-btn--active' : '']" @click="waTopupForm.currency = 'USD'">USD Direct</button>
                  </div>

                  <!-- EGP fields -->
                  <template v-if="waTopupForm.currency === 'EGP'">
                    <div class="form-row-2">
                      <div class="form-group-sm">
                        <label>EGP Received (gross, VAT incl.) <span class="req">*</span></label>
                        <input v-model.number="waTopupForm.amount_egp_gross" type="number" min="1" placeholder="e.g. 3000" />
                      </div>
                    </div>
                  </template>

                  <!-- USD fields -->
                  <template v-else>
                    <div class="form-group-sm">
                      <label>USD Amount (net, already deducted fees) <span class="req">*</span></label>
                      <input v-model.number="waTopupForm.amount_usd_direct" type="number" min="0.01" step="0.01" placeholder="e.g. 100.00" />
                      <p class="field-hint">Enter the exact USD amount to credit — no conversion applied.</p>
                    </div>
                  </template>

                  <div class="form-row-2">
                    <div class="form-group-sm">
                      <label>Payment Ref</label>
                      <input v-model="waTopupForm.payment_ref" type="text" placeholder="Bank transfer ref" />
                    </div>
                    <div class="form-group-sm">
                      <label>Notes</label>
                      <input v-model="waTopupForm.notes" type="text" placeholder="Optional notes" />
                    </div>
                  </div>

                  <!-- Live breakdown -->
                  <div v-if="waTopupBreakdown" class="wa-breakdown-box">
                    <template v-if="waTopupForm.currency === 'EGP'">
                      <div class="wa-bk-row">
                        <span>Gross</span><span>{{ waTopupBreakdown.egp_gross.toLocaleString('en', { minimumFractionDigits: 2 }) }} EGP</span>
                      </div>
                      <div class="wa-bk-row wa-bk-row--deduct">
                        <span>VAT (14%)</span><span>− {{ waTopupBreakdown.egp_vat.toLocaleString('en', { minimumFractionDigits: 2 }) }} EGP</span>
                      </div>
                      <div class="wa-bk-row">
                        <span>@ {{ waTopupBreakdown.rate }} EGP/USD</span><span>= ${{ waTopupBreakdown.usd_gross.toFixed(4) }}</span>
                      </div>
                      <div class="wa-bk-row wa-bk-row--deduct">
                        <span>Bank Fee (3%)</span><span>− ${{ waTopupBreakdown.bank_fee_usd.toFixed(4) }}</span>
                      </div>
                      <div class="wa-bk-row wa-bk-row--divider"></div>
                    </template>
                    <div class="wa-bk-row wa-bk-row--result">
                      <span>USD Credited to Meta</span><span>${{ waTopupBreakdown.usd_credited.toFixed(4) }}</span>
                    </div>
                  </div>

                  <button class="btn-submit btn-full mt-16" :disabled="waTopupLoading || (waTopupForm.currency === 'EGP' ? !waTopupForm.amount_egp_gross : !waTopupForm.amount_usd_direct)" @click="submitWaTopup">
                    <span v-if="waTopupLoading" class="spinner"></span>
                    {{ waTopupLoading ? 'Recording…' : 'Record Top-up' }}
                  </button>
                </div>
              </div>

              <!-- Top-up History -->
              <div v-if="waBillingDetail.topups.length > 0" class="wa-section-card">
                <h4 class="tab-section-title">Top-up History</h4>
                <div class="wa-topup-list">
                  <div v-for="t in waBillingDetail.topups" :key="t.id" class="wa-topup-item">
                    <div class="wa-topup-main">
                      <div class="wa-topup-amounts">
                        <span v-if="t.payment_currency === 'USD'" class="wa-topup-cur-badge wa-topup-cur-badge--usd">USD</span>
                        <span v-else class="wa-topup-cur-badge wa-topup-cur-badge--egp">EGP</span>
                        <span v-if="t.payment_currency === 'USD'" class="wa-topup-egp">${{ t.amount_usd_credited.toFixed(2) }} direct</span>
                        <span v-else class="wa-topup-egp">{{ t.amount_egp_gross.toLocaleString('en', { minimumFractionDigits: 2 }) }} EGP</span>
                        <span class="wa-topup-usd">→ ${{ t.amount_usd_credited.toFixed(2) }} credited</span>
                      </div>
                      <div class="wa-topup-remaining" :class="{ 'wa-topup-remaining--zero': t.amount_usd_remaining <= 0 }">
                        ${{ t.amount_usd_remaining.toFixed(2) }} left
                      </div>
                    </div>
                    <div class="wa-topup-meta">
                      <span>{{ t.created_at.slice(0, 10) }}</span>
                      <span v-if="t.payment_ref" class="wa-topup-ref">{{ t.payment_ref }}</span>
                      <span v-if="t.bank_fee_rate" class="text-muted">fee {{ (t.bank_fee_rate * 100).toFixed(1) }}%</span>
                      <span class="text-muted">@ {{ t.exchange_rate }} EGP</span>
                    </div>
                    <div v-if="t.notes" class="wa-topup-notes">{{ t.notes }}</div>
                  </div>
                </div>
              </div>

            </template>
          </div>

          <!-- ── ACCESS TAB ── -->
          <div v-if="activeTab === 'access'">
            <div class="access-section">
              <h4 class="tab-section-title">Subscription</h4>
              <div class="form-group">
                <label>Plan</label>
                <select v-model="editForm.plan_id" class="full-select">
                  <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Start Date</label>
                <input v-model="editForm.from_date" type="date" />
              </div>
              <button class="btn-submit btn-full" @click="savePlan">Update Subscription</button>
            </div>

            <div class="access-section access-section--danger">
              <h4 class="tab-section-title danger-label">Danger Zone</h4>
              <div class="danger-row">
                <div>
                  <div class="danger-action-title">Suspend Account</div>
                  <div class="danger-action-sub">Prevents login without deleting any data.</div>
                </div>
                <button class="btn-danger-outline" @click="suspendAccount">Suspend</button>
              </div>
              <div class="danger-row">
                <div>
                  <div class="danger-action-title">Delete Account</div>
                  <div class="danger-action-sub">Permanently removes this account and all associated data.</div>
                </div>
                <button class="btn-danger" @click="deleteAccount">Delete</button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </transition>

    <!-- ─────────────────────────────────────────────
         BULK CREATE OVERLAY + DRAWER
    ──────────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="showBulk" class="drawer-overlay" @click="closeBulk" />
    </transition>
    <transition name="drawer">
      <div v-if="showBulk" class="drawer drawer--bulk">

        <!-- BULK HEADER -->
        <div class="drawer-header">
          <div class="drawer-header-info">
            <div class="bulk-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
            </div>
            <div>
              <h2 class="drawer-title">Bulk Account Creation</h2>
              <p class="drawer-sub" v-if="bulkPhase === 'form'">{{ bulkRows.length }} account{{ bulkRows.length !== 1 ? 's' : '' }} to create &mdash; each row is independent</p>
              <p class="drawer-sub" v-else>{{ bulkResults.filter(r => r.status === 'success').length }} of {{ bulkResults.length }} created successfully</p>
            </div>
          </div>
          <button class="drawer-close" @click="closeBulk">&times;</button>
        </div>

        <!-- ── FORM PHASE ── -->
        <template v-if="bulkPhase === 'form'">
          <div class="bulk-toolbar">
            <div class="row-counter">
              <button class="btn-counter" @click="removeLast" :disabled="bulkRows.length <= 2" title="Remove last row">−</button>
              <span class="counter-val">{{ bulkRows.length }} rows</span>
              <button class="btn-counter" @click="addBulkRow" title="Add row">+</button>
            </div>
            <button class="btn-import" @click="$refs.fileImport.click()">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 8h14v2H5z"/></svg>
              Import XLSX / CSV
            </button>
            <input ref="fileImport" type="file" accept=".xlsx,.xls,.csv" style="display:none" @change="importFromFile" />
          </div>
          <div v-if="importMsg" :class="['import-msg', 'import-msg--' + importMsgType]">{{ importMsg }}</div>

          <!-- CSV / XLSX field reference -->
          <div class="import-fields">
            <span class="import-fields-label">Expected columns:</span>
            <span class="import-field import-field--required" title="Required">Name</span>
            <span class="import-field import-field--required" title="Required">Company</span>
            <span class="import-field import-field--required" title="Required">Phone</span>
            <span class="import-field import-field--required" title="Required">Email</span>
            <span class="import-field" title="Optional — e.g. 2 for Egypt">Country Code</span>
            <span class="import-field" title="Optional — matched by plan name">Plan</span>
            <span class="import-field" title="Optional — YYYY-MM-DD">Start Date</span>
            <span class="import-fields-note"><span class="import-field--required import-req-dot"></span> required</span>
          </div>

          <div class="bulk-scroll">
            <table class="bulk-table">
              <thead>
                <tr>
                  <th class="col-num">#</th>
                  <th class="col-name">Full Name *</th>
                  <th class="col-company">Company *</th>
                  <th class="col-phone">Phone *</th>
                  <th class="col-email">Email *</th>
                  <th class="col-plan">Plan *</th>
                  <th class="col-date">Start Date</th>
                  <th class="col-remove"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in bulkRows" :key="row.id" :class="['bulk-row', { 'bulk-row--error': row._error || row._phoneError || bulkDupPhones.has(row.country_code + '-' + row.phone.trim()) }]">
                  <td class="col-num"><span class="row-num">{{ idx + 1 }}</span></td>
                  <td class="col-name">
                    <input v-model="row.name" class="bulk-input" placeholder="Name" @input="revalidateRow(row)" />
                  </td>
                  <td class="col-company">
                    <input v-model="row.company_name" class="bulk-input" placeholder="Company" @input="revalidateRow(row)" />
                  </td>
                  <td class="col-phone">
                    <div class="bulk-phone-row">
                      <select v-model="row.country_code" class="bulk-cc" @change="onBulkPhoneChange(row)">
                        <option v-for="c in countryCodes" :key="c.code" :value="c.code">+{{ c.code }}</option>
                      </select>
                      <input v-model="row.phone" class="bulk-input bulk-phone-num" placeholder="Number" @input="onBulkPhoneChange(row)" />
                    </div>
                  </td>
                  <td class="col-email">
                    <input v-model="row.email" class="bulk-input" placeholder="email@company.com" type="email" @input="revalidateRow(row)" />
                  </td>
                  <td class="col-plan">
                    <select v-model="row.plan_id" class="bulk-select" @change="revalidateRow(row)">
                      <option value="">— Plan —</option>
                      <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </td>
                  <td class="col-date">
                    <input v-model="row.from_date" class="bulk-input" type="date" />
                  </td>
                  <td class="col-remove">
                    <button class="btn-remove-row" @click="removeBulkRow(row.id)" :disabled="bulkRows.length <= 2" title="Remove row">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- per-row inline errors -->
            <div v-for="(row, idx) in bulkRows" :key="'e' + row.id">
              <div v-if="row._phoneError || row._error || bulkDupPhones.has(row.country_code + '-' + row.phone.trim())" class="bulk-row-error">
                Row {{ idx + 1 }}: {{ row._phoneError || (bulkDupPhones.has(row.country_code + '-' + row.phone.trim()) ? 'Duplicate phone number in batch' : row._error) }}
              </div>
            </div>
          </div>

          <!-- Validation status bar -->
          <div class="bulk-status-bar">
            <template v-if="!bulkFieldsValid">
              <span class="bulk-status bulk-status--idle">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" opacity=".3"/><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                {{ bulkDupPhones.size > 0 ? 'Fix duplicate phone numbers' : 'Fill all required fields to continue' }}
              </span>
            </template>
            <template v-else-if="bulkPhoneCheckState === 'checking'">
              <span class="bulk-status bulk-status--checking">
                <span class="spinner spinner--dark"></span>
                Checking phone numbers…
              </span>
            </template>
            <template v-else-if="bulkPhoneCheckState === 'ok'">
              <span class="bulk-status bulk-status--ok">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                All good — ready to create
              </span>
            </template>
            <template v-else-if="bulkPhoneCheckState === 'error'">
              <span class="bulk-status bulk-status--error">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                Fix highlighted phone errors above
              </span>
            </template>
          </div>

          <div class="drawer-footer">
            <button class="btn-cancel" @click="closeBulk">Cancel</button>
            <button class="btn-submit" @click="submitBulk" :disabled="!bulkReadyToCreate || bulkLoading">
              <span v-if="bulkLoading" class="spinner" />
              <span v-else>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:-2px"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              </span>
              {{ bulkLoading ? 'Creating…' : 'Create ' + bulkRows.length + ' Account' + (bulkRows.length !== 1 ? 's' : '') }}
            </button>
          </div>
        </template>

        <!-- ── RESULTS PHASE ── -->
        <template v-if="bulkPhase === 'results'">
          <div class="bulk-scroll bulk-results-list">
            <div v-for="(r, idx) in bulkResults" :key="idx" class="result-item" :class="'result-item--' + r.status">
              <div class="result-avatar" :style="{ background: avatarColor(r.name) }">{{ initials(r.name) }}</div>
              <div class="result-info">
                <div class="result-name">{{ r.name }}</div>
                <div class="result-company">{{ r.company_name }} &middot; <span class="result-plan">{{ r.plan_name }}</span></div>
              </div>
              <div v-if="r.status === 'success'" class="result-pw-cell">
                <span class="result-pw-dots">{{ r.password }}</span>
                <button class="btn-copy-pw" :class="{ 'btn-copy-pw--done': copiedResultIdx === idx }" @click="copyResultPassword(idx)" :title="copiedResultIdx === idx ? 'Copied!' : 'Copy password'">
                  <svg v-if="copiedResultIdx !== idx" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                  <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                </button>
              </div>
              <div class="result-status">
                <span v-if="r.status === 'success'" class="result-badge result-badge--ok">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                  Created
                </span>
                <span v-else class="result-badge result-badge--err">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                  {{ r.message || 'Failed' }}
                </span>
              </div>
            </div>
          </div>

          <div class="drawer-footer">
            <button class="btn-cancel btn-download" @click="downloadCredentialsCsv">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 8h14v2H5z"/></svg>
              Download CSV
            </button>
            <button class="btn-submit" @click="closeBulk">Done</button>
          </div>
        </template>

      </div>
    </transition>

    <!-- ═══ QUICK CREDIT MODAL ═══ -->
    <transition name="fade">
      <div v-if="showQuickCredit" class="modal-overlay" @click.self="closeQuickCredit">
        <div class="credit-modal credit-modal--quick">

          <!-- Header -->
          <div class="credit-modal-header">
            <div style="display:flex;align-items:center;gap:10px;">
              <div class="drawer-avatar" :style="{ background: quickCreditAccount ? avatarColor(quickCreditAccount.name) : '#4f46e5' }">
                {{ quickCreditAccount ? initials(quickCreditAccount.name) : '' }}
              </div>
              <div>
                <h3 style="margin:0;font-size:15px;font-weight:700;color:var(--text-primary)">{{ quickCreditAccount && quickCreditAccount.name }}</h3>
                <p style="margin:2px 0 0;font-size:12px;color:var(--text-secondary)">{{ quickCreditAccount && quickCreditAccount.company_name }}</p>
              </div>
            </div>
            <button class="drawer-close" @click="closeQuickCredit">&times;</button>
          </div>

          <!-- Body -->
          <div class="credit-modal-body">
            <!-- Balance -->
            <div class="credit-balance-box">
              <div class="credit-balance-label">Current Balance</div>
              <div class="credit-balance-val">
                {{ quickCreditAccount ? (quickCreditAccount.credit || 0).toLocaleString() : 0 }}<span class="credit-unit">cr</span>
              </div>
            </div>

            <!-- Presets -->
            <div class="credit-presets">
              <button
                v-for="p in creditPresets"
                :key="p"
                :class="['credit-preset-btn', quickCreditAmount === p ? 'credit-preset-btn--active' : '']"
                @click="quickCreditAmount = p"
              >{{ p.toLocaleString() }}</button>
            </div>

            <!-- Custom input -->
            <input
              v-model.number="quickCreditAmount"
              type="number"
              min="1"
              placeholder="Or type a custom amount…"
              class="credit-custom-input"
            />
          </div>

          <!-- Footer -->
          <div class="credit-modal-footer">
            <button class="btn-cancel" @click="closeQuickCredit">Cancel</button>
            <button
              class="btn-submit"
              :disabled="!quickCreditAmount || quickCreditLoading"
              @click="submitQuickCredit"
            >
              <span v-if="quickCreditLoading" class="spinner"></span>
              {{ quickCreditLoading ? 'Adding…' : 'Add ' + (quickCreditAmount ? Number(quickCreditAmount).toLocaleString() : 0) + ' Credits' }}
            </button>
          </div>

        </div>
      </div>
    </transition>

    <!-- ═══ CHANGE PLAN MODAL ═══ -->
    <transition name="fade">
      <div v-if="showChangePlan" class="modal-overlay" @click.self="closeChangePlan">
        <div class="credit-modal credit-modal--quick">

          <!-- Header -->
          <div class="credit-modal-header">
            <div style="display:flex;align-items:center;gap:10px;">
              <div class="drawer-avatar" :style="{ background: changePlanAccount ? avatarColor(changePlanAccount.name) : '#4f46e5' }">
                {{ changePlanAccount ? initials(changePlanAccount.name) : '' }}
              </div>
              <div>
                <h3 style="margin:0;font-size:15px;font-weight:700;color:var(--text-primary)">{{ changePlanAccount && changePlanAccount.name }}</h3>
                <p style="margin:2px 0 0;font-size:12px;color:var(--text-secondary)">
                  Current: <strong>{{ changePlanAccount && changePlanAccount.plan_name || '—' }}</strong>
                </p>
              </div>
            </div>
            <button class="drawer-close" @click="closeChangePlan">&times;</button>
          </div>

          <!-- Body -->
          <div class="credit-modal-body">
            <div class="form-group">
              <label>New Plan <span class="req">*</span></label>
              <select v-model="changePlanId" class="full-select">
                <option value="">— Select plan —</option>
                <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.name }} — {{ Number(p.price).toLocaleString() }} EGP</option>
              </select>
            </div>
            <div class="form-group">
              <label>Start Date <span class="req">*</span></label>
              <input v-model="changePlanDate" type="date" />
            </div>
            <div v-if="changePlanId" class="plan-change-summary">
              Price: <strong>{{ Number((plans.find(p => p.id === changePlanId) || {}).price || 0).toLocaleString() }} EGP</strong>
              — a payment record will be created automatically.
            </div>
          </div>

          <!-- Footer -->
          <div class="credit-modal-footer">
            <button class="btn-cancel" @click="closeChangePlan">Cancel</button>
            <button
              class="btn-submit"
              :disabled="!changePlanId || !changePlanDate || changePlanLoading"
              @click="submitChangePlan"
            >
              <span v-if="changePlanLoading" class="spinner"></span>
              {{ changePlanLoading ? 'Saving…' : 'Apply Plan' }}
            </button>
          </div>

        </div>
      </div>
    </transition>

    <!-- WA BILLING CONFIG MODAL -->
    <div v-if="waShowBillingConfig" class="modal-overlay" @click.self="waShowBillingConfig = false">
      <div class="credit-modal">
        <div class="credit-modal-header">
          <div style="display:flex;align-items:center;gap:8px;">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="color:#4f46e5;flex-shrink:0"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6zm0 4h8v2H6zm10 0h2v2h-2zm-6-4h8v2h-8z"/></svg>
            <h3>WA Billing Configuration</h3>
          </div>
          <div style="display:flex;align-items:center;gap:6px;">
            <span v-if="waPlatformSettings.bsp_token_set" class="wa-badge wa-badge--active">BSP Token OK</span>
            <span v-else class="wa-badge wa-badge--revoked">Token missing</span>
            <span class="wa-badge wa-badge--none" title="Requires Meta Solution Partner status">Credit Line (BSP)</span>
            <button class="drawer-close" @click="waShowBillingConfig = false">&times;</button>
          </div>
        </div>

        <div class="credit-modal-body">
          <div class="form-row-2">
            <div class="form-group-sm">
              <label>Credit Line ID <span style="font-size:10px;color:var(--text-tertiary)">(BSP only — not yet provisioned)</span></label>
              <div style="display:flex;align-items:center;gap:8px;">
                <code style="flex:1;padding:6px 8px;background:var(--bg-tertiary);border-radius:6px;font-size:12px;border:1px solid var(--border-color);display:block;word-break:break-all;opacity:.5">{{ waPlatformSettings.meta_credit_line_id || '—' }}</code>
                <button class="btn-icon-sm" disabled title="Requires Meta Solution Partner status">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
                </button>
              </div>
              <p class="field-hint" style="color:var(--text-tertiary)">Credit line sharing requires Meta Solution Partner status. Pending upgrade.</p>
            </div>
            <div class="form-group-sm">
              <label>Markup Rate (EGP/USD)</label>
              <input v-model="waPlatformSettings.usd_to_egp_rate" type="number" step="0.01" placeholder="53.5" />
              <p class="field-hint">Used when converting EGP payments to USD credit.</p>
            </div>
          </div>
        </div>

        <div class="credit-modal-footer">
          <button class="btn-submit" :disabled="waSaveSettingsLoading" @click="saveWaPlatformSettings">
            <span v-if="waSaveSettingsLoading" class="spinner"></span>
            {{ waSaveSettingsLoading ? 'Saving…' : 'Update Rate' }}
          </button>
          <button class="btn-secondary" style="margin-left:auto" @click="waShowBillingConfig = false">Close</button>
        </div>
      </div>
    </div>

    <!-- TOAST -->
    <transition name="toast">
      <div v-if="toast.show" :class="['toast', 'toast--' + toast.type]">{{ toast.message }}</div>
    </transition>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { countryCodes, DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'

const API = 'https://api.quali-d.com'

export default {
  name: 'AdminAccountsView',
  components: { Sidebar, TopHeader },

  data () {
    const today = new Date().toISOString().split('T')[0]
    return {
      countryCodes,
      search: '',
      filterStatus: '',
      filterPlan: '',

      drawerOpen: false,
      drawerMode: 'new', // 'new' | 'detail'
      selectedAccount: null,
      activeTab: 'account',
      passwordCopied: false,
      showAddPayment: false,
      newFormLoading: false,
      newFormError: '',
      newFormPhoneError: '',

      showBulk: false,
      bulkPhase: 'form', // 'form' | 'results'
      bulkLoading: false,
      bulkRows: [],
      bulkResults: [],
      copiedResultIdx: null,
      bulkPhoneCheckState: 'idle', // 'idle' | 'checking' | 'ok' | 'error'
      bulkPhoneCheckTimer: null,
      importMsg: '',
      importMsgType: 'info', // 'info' | 'error'

      // ── Credits ──────────────────────────────────────
      creditPresets: [500, 1000, 5000, 10000, 50000, 100000, 500000, 999999],
      addCreditAmount: null,
      addCreditLoading: false,
      // change-plan modal
      showChangePlan: false,
      changePlanAccount: null,
      changePlanId: '',
      changePlanDate: today,
      changePlanLoading: false,

      // quick-credit modal
      showQuickCredit: false,
      quickCreditAccount: null,
      quickCreditAmount: null,
      quickCreditLoading: false,

      detailTabs: [
        { id: 'account', label: 'Account' },
        { id: 'company', label: 'Company' },
        { id: 'payments', label: 'Payments' },
        { id: 'credits', label: 'Credits' },
        { id: 'wa_billing', label: 'WA Billing' },
        { id: 'access', label: 'Plan' }
      ],

      weekDays: [
        { val: 'Mon', label: 'Mon' },
        { val: 'Tue', label: 'Tue' },
        { val: 'Wed', label: 'Wed' },
        { val: 'Thu', label: 'Thu' },
        { val: 'Fri', label: 'Fri' },
        { val: 'Sat', label: 'Sat' },
        { val: 'Sun', label: 'Sun' }
      ],

      newForm: {
        name: '',
        company_name: '',
        country_code: DEFAULT_COUNTRY_CODE,
        phone: '',
        email: '',
        password: '',
        planMode: 'standard',
        plan_id: '',
        enterprise: { name: 'Enterprise', price: '', plan_type: 'monthly', agents_limit: '', credit_amount: '' },
        from_date: today,
        paid: false,
        amount: '',
        notes: ''
      },

      editForm: {
        name: '',
        company_name: '',
        email: '',
        phone: '',
        newPassword: '',
        address: '',
        company_phone: '',
        work_hours_from: '09:00',
        work_hours_to: '17:00',
        work_days: [],
        location_url: '',
        notes: '',
        plan_id: '',
        from_date: today
      },

      addPaymentForm: {
        plan_id: '', amount: '', from_date: today, notes: ''
      },

      toast: { show: false, message: '', type: 'success' },

      pageLoading: false,
      pageError: '',

      plans: [],
      accounts: [],

      // WA Billing
      waBillingMap: {},
      waBillingDetail: null,
      waBillingDetailLoading: false,
      waShareLoading: false,
      waEnableLoading: false,
      waRevokeLoading: false,
      waShowTopupForm: false,
      waTopupLoading: false,
      waTopupForm: {
        currency: 'EGP',
        amount_egp_gross: '',
        amount_usd_direct: '',
        payment_ref: '',
        notes: ''
      },
      waShowBillingConfig: false,
      waSaveSettingsLoading: false,
      waVerifyLoading: false,
      waCreditLineVerification: null,
      waPlatformSettings: {
        bsp_token_set: false,
        meta_credit_line_id: '',
        usd_to_egp_rate: ''
      }
    }
  },

  computed: {
    filteredAccounts () {
      const q = this.search.toLowerCase().trim()
      return this.accounts.filter(a => {
        const matchSearch = !q ||
          a.name.toLowerCase().includes(q) ||
          a.company_name.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          String(a.phone).includes(q)
        let matchStatus = true
        if (this.filterStatus === 'expiring') {
          const diff = a.expires ? (new Date(a.expires) - new Date()) / 86400000 : null
          matchStatus = diff !== null && diff >= 0 && diff <= 10
        } else if (this.filterStatus === 'recently-expired') {
          const diff = a.expires ? (new Date() - new Date(a.expires)) / 86400000 : null
          matchStatus = diff !== null && diff >= 0 && diff <= 10
        } else if (this.filterStatus) {
          matchStatus = a.status === this.filterStatus
        }
        const matchPlan = !this.filterPlan || a.plan_id === this.filterPlan
        return matchSearch && matchStatus && matchPlan
      })
    },

    newFormPlanSelected () {
      if (this.newForm.planMode === 'standard') return !!this.newForm.plan_id
      return !!this.newForm.enterprise.price
    },

    // Signature of bulk row inputs (excludes _error/_phoneError to avoid watcher loops)
    bulkInputSignature () {
      return this.bulkRows.map(r =>
        [r.country_code, r.phone, r.name, r.company_name, r.email, r.plan_id, r.from_date].join('|')
      ).join('\n')
    },

    // Set of "cc-phone" keys that appear more than once in the batch
    bulkDupPhones () {
      const seen = {}
      const dups = new Set()
      this.bulkRows.forEach(r => {
        const phone = (r.phone || '').trim()
        if (!phone) return
        const key = r.country_code + '-' + phone
        if (seen[key]) dups.add(key)
        else seen[key] = true
      })
      return dups
    },

    // True when all rows pass field validation AND no within-batch phone duplicates
    bulkFieldsValid () {
      return this.bulkRows.every(r => !this.validateBulkRow(r)) && this.bulkDupPhones.size === 0
    },

    // True only when field-valid AND API phone check passed
    bulkReadyToCreate () {
      return this.bulkFieldsValid && this.bulkPhoneCheckState === 'ok'
    },

    stats () {
      const today = new Date()
      const in7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      return {
        total: this.accounts.length,
        active: this.accounts.filter(a => a.status === 'Active').length,
        pending: this.accounts.filter(a => a.status === 'Pending').length,
        expiringSoon: this.accounts.filter(a => {
          if (!a.expires) return false
          const d = new Date(a.expires)
          return d >= today && d <= in7Days
        }).length,
        expired: this.accounts.filter(a => a.status === 'Expired').length
      }
    },

    waTopupBreakdown () {
      if (this.waTopupForm.currency === 'USD') {
        const usd = parseFloat(this.waTopupForm.amount_usd_direct)
        if (!usd || usd <= 0) return null
        return { usd_credited: +usd.toFixed(4) }
      }
      const gross = parseFloat(this.waTopupForm.amount_egp_gross)
      if (!gross || gross <= 0) return null
      const vatRate = 0.14
      const egpVat = +(gross - gross / (1 + vatRate)).toFixed(2)
      const egpNet = +(gross - egpVat).toFixed(2)
      const rate = parseFloat(this.waPlatformSettings.usd_to_egp_rate) || 53.5
      const usdGross = +(egpNet / rate).toFixed(4)
      const bankFeeUsd = +(usdGross * 0.03).toFixed(4)
      const usdCredited = +(usdGross - bankFeeUsd).toFixed(4)
      return { egp_gross: gross, egp_vat: egpVat, egp_net: egpNet, rate, usd_gross: usdGross, bank_fee_usd: bankFeeUsd, usd_credited: usdCredited }
    }
  },

  watch: {
    bulkInputSignature () {
      // Reset phone check whenever any input field changes
      clearTimeout(this.bulkPhoneCheckTimer)
      this.bulkPhoneCheckState = 'idle'
      if (this.importMsg) this.importMsg = ''
      if (this.bulkFieldsValid) {
        this.bulkPhoneCheckTimer = setTimeout(() => this.runBulkPhoneCheck(), 700)
      }
    },

    activeTab (tab) {
      if (tab === 'wa_billing') {
        this.loadWaBillingDetail()
      }
    },

    'newForm.plan_id' (planId) {
      if (!planId) { this.newForm.paid = false; return }
      if (this.newForm.paid) {
        const plan = this.plans.find(p => p.id === planId)
        if (plan) this.newForm.amount = plan.price
      }
    },
    'newForm.paid' (val) {
      if (val && this.newForm.plan_id) {
        const plan = this.plans.find(p => p.id === this.newForm.plan_id)
        if (plan) this.newForm.amount = plan.price
      }
    }
  },

  methods: {
    // ── API helpers ──────────────────────────────────
    apiHeaders () {
      const token = localStorage.getItem('token')
      return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    },

    mapUser (u) {
      const cp = u.company_profile || {}
      const workDays = cp.work_days
        ? (typeof cp.work_days === 'string' ? cp.work_days.split(',').map(d => d.trim()).filter(Boolean) : cp.work_days)
        : []
      const parseHours = (str) => {
        if (!str) return { from: '09:00', to: '17:00' }
        const parts = str.split('-')
        return { from: parts[0] || '09:00', to: parts[1] || '17:00' }
      }
      const hours = parseHours(cp.work_hours)
      // Backend may return phone as "+{country_code}{number}" — strip the prefix so
      // the UI can display the country code and number independently.
      const cc = String(u.country_code || '20')
      let rawPhone = String(u.phone || '')
      const prefix = '+' + cc
      if (rawPhone.startsWith(prefix)) rawPhone = rawPhone.slice(prefix.length)
      else if (rawPhone.startsWith('+')) rawPhone = rawPhone.slice(1)

      return {
        id: u.user_id,
        name: u.name,
        company_name: u.company_name,
        email: u.email,
        phone: rawPhone,
        country_code: cc,
        plan_name: u.subscription ? u.subscription.plan_name : '—',
        plan_id: u.subscription ? u.subscription.plan_id : null,
        status: u.subscription ? u.subscription.status : 'Pending',
        expires: u.subscription ? u.subscription.end_date : null,
        created_at: u.registered_at,
        company_profile: {
          address: cp.address || '',
          phone: cp.phone || '',
          work_hours_from: hours.from,
          work_hours_to: hours.to,
          work_days: workDays,
          location_url: cp.location_url || '',
          notes: cp.notes || ''
        },
        payments: (u.payment_history ? u.payment_history.payments || [] : []).map(p => ({
          id: p.id,
          date: (p.created_at || '').slice(0, 10),
          plan: u.subscription ? u.subscription.plan_name : '—',
          amount: Number(p.amount),
          ref: 'PAYMENT-' + p.id,
          method: 'Bank Transfer'
        }))
      }
    },

    async loadData () {
      this.pageLoading = true
      this.pageError = ''
      try {
        const h = this.apiHeaders()
        const [plansRes, usersRes, creditsRes, waBillingRes] = await Promise.all([
          fetch(`${API}/admin/financial/plans`, { headers: h }),
          fetch(`${API}/admin/financial/users-subscriptions`, { headers: h }),
          fetch(`${API}/admin/financial/credits`, { headers: h }),
          fetch(`${API}/admin/wa-billing/bulk-status`, { headers: h })
        ])
        if (plansRes.status === 403 || usersRes.status === 403) {
          this.pageError = 'Access denied. Admin only.'
          return
        }
        const [plansData, usersData, creditsData, waBillingData] = await Promise.all([
          plansRes.json(), usersRes.json(), creditsRes.json(), waBillingRes.json()
        ])
        if (plansData.success) this.plans = plansData.plans
        const creditsMap = {}
        if (creditsData.success && creditsData.credits) {
          creditsData.credits.forEach(c => { creditsMap[c.owner] = Number(c.balance) })
        }
        if (waBillingData.success && waBillingData.data) {
          this.waBillingMap = waBillingData.data
        }
        this.loadWaPlatformSettings()
        this.accounts = (usersData.data || []).map(u => {
          const acc = this.mapUser(u)
          acc.credit = creditsMap[u.user_id] || 0
          return acc
        })
      } catch (e) {
        this.pageError = 'Failed to load accounts: ' + e.message
      } finally {
        this.pageLoading = false
      }
    },

    // ── Drawer control ──────────────────────────────
    openNew () {
      this.newForm.password = this.generatePassword()
      this.passwordCopied = false
      this.newFormError = ''
      this.newFormLoading = false
      this.drawerMode = 'new'
      this.drawerOpen = true
    },

    openDetail (acc) {
      this.selectedAccount = acc
      this.activeTab = 'account'
      this.showAddPayment = false
      this.waBillingDetail = null
      this.waShowTopupForm = false
      this.waTopupForm = { currency: 'EGP', amount_egp_gross: '', amount_usd_direct: '', payment_ref: '', notes: '' }
      this.editForm = {
        name: acc.name,
        company_name: acc.company_name,
        email: acc.email,
        phone: acc.phone,
        newPassword: '',
        address: acc.company_profile.address,
        company_phone: acc.company_profile.phone,
        work_hours_from: acc.company_profile.work_hours_from || '09:00',
        work_hours_to: acc.company_profile.work_hours_to || '17:00',
        work_days: [...(acc.company_profile.work_days || [])],
        location_url: acc.company_profile.location_url,
        notes: acc.company_profile.notes,
        plan_id: acc.plan_id,
        from_date: new Date().toISOString().split('T')[0]
      }
      this.addPaymentForm = {
        plan_id: '', amount: '', from_date: new Date().toISOString().split('T')[0], notes: ''
      }
      this.drawerMode = 'detail'
      this.drawerOpen = true
    },

    closeDrawer () {
      this.drawerOpen = false
      setTimeout(() => { this.selectedAccount = null }, 300)
    },

    // ── Password helpers ────────────────────────────
    generatePassword () {
      const lower = 'abcdefghijklmnopqrstuvwxyz'
      const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const digits = '0123456789'
      const all = lower + upper + digits
      const pwd = [
        lower[Math.floor(Math.random() * lower.length)],
        upper[Math.floor(Math.random() * upper.length)],
        digits[Math.floor(Math.random() * digits.length)]
      ]
      for (let i = 3; i < 12; i++) pwd.push(all[Math.floor(Math.random() * all.length)])
      return pwd.sort(() => Math.random() - 0.5).join('')
    },

    regeneratePassword () {
      this.newForm.password = this.generatePassword()
      this.passwordCopied = false
    },

    regenerateEditPassword () {
      this.editForm.newPassword = this.generatePassword()
    },

    async copyPassword () {
      try {
        await navigator.clipboard.writeText(this.newForm.password)
        this.passwordCopied = true
        setTimeout(() => { this.passwordCopied = false }, 2000)
      } catch { /* silent */ }
    },

    async checkNewFormPhone () {
      const phone = (this.newForm.phone || '').trim()
      if (!phone) return
      try {
        const res = await fetch(`${API}/admin/financial/check-phones`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({ phones: [{ country_code: this.newForm.country_code, phone }] })
        })
        const data = await res.json()
        if (data.success && data.taken.length > 0) {
          this.newFormPhoneError = 'This phone number is already registered.'
        } else {
          this.newFormPhoneError = ''
        }
      } catch { /* silent — backend will also block duplicates on submit */ }
    },

    // ── New account submit ───────────────────────────
    async submitNew () {
      this.newFormError = ''
      const f = this.newForm
      if (!f.name || !f.company_name || !f.phone || !f.email) {
        this.newFormError = 'Please fill all required fields.'
        return
      }
      if (this.newFormPhoneError) {
        this.newFormError = this.newFormPhoneError
        return
      }
      if (f.planMode === 'standard' && !f.plan_id) {
        this.newFormError = 'Please select a plan.'
        return
      }
      if (f.planMode === 'enterprise' && (!f.enterprise.price || !f.enterprise.agents_limit || !f.enterprise.credit_amount)) {
        this.newFormError = 'Please fill all enterprise plan fields.'
        return
      }

      this.newFormLoading = true
      try {
        const body = {
          name: f.name,
          company_name: f.company_name,
          country_code: f.country_code,
          phone: f.phone,
          email: f.email,
          password: f.password,
          plan_id: f.plan_id,
          from_date: f.from_date,
          paid: f.paid,
          amount: f.paid ? Number(f.amount) : 0,
          notes: f.notes || ''
        }
        const res = await fetch(`${API}/admin/financial/create-user`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!data.success) {
          this.newFormError = data.message || 'Failed to create account.'
          return
        }
        this.closeDrawer()
        this.showToast(`Account for ${f.company_name} created successfully`, 'success')
        await this.loadData()
      } catch (e) {
        this.newFormError = 'Network error: ' + e.message
      } finally {
        this.newFormLoading = false
      }
    },

    // ── Detail saves ─────────────────────────────────
    async saveAccount () {
      try {
        const res = await fetch(`${API}/admin/financial/users/${this.selectedAccount.id}`, {
          method: 'PUT',
          headers: this.apiHeaders(),
          body: JSON.stringify({
            name: this.editForm.name,
            company_name: this.editForm.company_name,
            email: this.editForm.email,
            phone: this.editForm.phone,
            password: this.editForm.newPassword || undefined
          })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Save failed', 'error'); return }
        this.selectedAccount.name = this.editForm.name
        this.selectedAccount.company_name = this.editForm.company_name
        this.selectedAccount.email = this.editForm.email
        this.selectedAccount.phone = this.editForm.phone
        this.showToast('Account info saved', 'success')
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      }
    },

    async saveCompany () {
      const ef = this.editForm
      const workHours = ef.work_hours_from + '-' + ef.work_hours_to
      const workDays = ef.work_days.join(',')
      try {
        const res = await fetch(`${API}/admin/financial/users/${this.selectedAccount.id}/company-profile`, {
          method: 'PUT',
          headers: this.apiHeaders(),
          body: JSON.stringify({
            address: ef.address,
            phone: ef.company_phone,
            work_hours: workHours,
            work_days: workDays,
            location_url: ef.location_url,
            notes: ef.notes
          })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Save failed', 'error'); return }
        this.selectedAccount.company_profile = {
          address: ef.address,
          phone: ef.company_phone,
          work_hours_from: ef.work_hours_from,
          work_hours_to: ef.work_hours_to,
          work_days: [...ef.work_days],
          location_url: ef.location_url,
          notes: ef.notes
        }
        this.showToast('Company profile saved', 'success')
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      }
    },

    async savePayment () {
      if (!this.addPaymentForm.amount) {
        this.showToast('Please enter an amount', 'error')
        return
      }
      try {
        const res = await fetch(`${API}/admin/financial/add-payment`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({
            user_id: this.selectedAccount.id,
            plan_id: this.addPaymentForm.plan_id || this.selectedAccount.plan_id,
            amount: Number(this.addPaymentForm.amount),
            from_date: this.addPaymentForm.from_date,
            notes: this.addPaymentForm.notes || ''
          })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Payment failed', 'error'); return }
        this.showAddPayment = false
        this.addPaymentForm = { plan_id: '', amount: '', from_date: new Date().toISOString().split('T')[0], notes: '' }
        this.showToast('Payment recorded', 'success')
        await this.loadData()
        const updated = this.accounts.find(a => a.id === this.selectedAccount.id)
        if (updated) {
          this.selectedAccount.payments = updated.payments
          this.selectedAccount.status = updated.status
          this.selectedAccount.expires = updated.expires
        }
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      }
    },

    async savePlan () {
      const plan = this.plans.find(p => p.id === this.editForm.plan_id)
      if (!plan) return
      try {
        const res = await fetch(`${API}/admin/financial/add-payment`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({
            user_id: this.selectedAccount.id,
            plan_id: plan.id,
            amount: Number(plan.price),
            from_date: this.editForm.from_date,
            notes: 'Plan change via admin'
          })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Update failed', 'error'); return }
        this.selectedAccount.plan_name = plan.name
        this.selectedAccount.plan_id = plan.id
        this.showToast('Subscription updated', 'success')
        await this.loadData()
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      }
    },

    // ── WA Billing methods ───────────────────────────────────────────────────

    async loadWaPlatformSettings () {
      try {
        const res = await fetch(`${API}/admin/wa-billing/settings`, { headers: this.apiHeaders() })
        const data = await res.json()
        if (data.success) {
          this.waPlatformSettings.bsp_token_set = data.settings.bsp_token_set
          this.waPlatformSettings.meta_credit_line_id = data.settings.meta_credit_line_id || ''
          this.waPlatformSettings.usd_to_egp_rate = data.settings.usd_to_egp_rate || ''
          this.verifyCreditLineId(true)
        }
      } catch { /* silent */ }
    },

    async saveWaPlatformSettings () {
      this.waSaveSettingsLoading = true
      try {
        const res = await fetch(`${API}/admin/wa-billing/settings`, {
          method: 'PUT',
          headers: this.apiHeaders(),
          body: JSON.stringify({
            usd_to_egp_rate: this.waPlatformSettings.usd_to_egp_rate
          })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Save failed', 'error'); return }
        this.showToast('Settings saved', 'success')
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      } finally {
        this.waSaveSettingsLoading = false
      }
    },

    async verifyCreditLineId (silent = false) {
      const id = (this.waPlatformSettings.meta_credit_line_id || '').trim()
      if (!id) return
      this.waVerifyLoading = true
      this.waCreditLineVerification = null
      try {
        const res = await fetch(`${API}/admin/wa-billing/verify-credit-line/${id}`, {
          headers: this.apiHeaders()
        })
        const data = await res.json()
        this.waCreditLineVerification = data
        if (!data.success && !silent) {
          this.showToast(data.message || 'Verification failed', 'error')
        }
      } catch (e) {
        this.waCreditLineVerification = { success: false, message: 'Network error: ' + e.message }
      } finally {
        this.waVerifyLoading = false
      }
    },

    async loadWaBillingDetail () {
      if (!this.selectedAccount) return
      this.waBillingDetailLoading = true
      try {
        const res = await fetch(`${API}/admin/wa-billing/status/${this.selectedAccount.id}`, { headers: this.apiHeaders() })
        const data = await res.json()
        if (data.success) {
          this.waBillingDetail = data
        } else {
          this.showToast(data.message || 'Failed to load WA billing detail', 'error')
        }
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      } finally {
        this.waBillingDetailLoading = false
      }
    },

    async shareCreditLine () {
      this.waShareLoading = true
      try {
        const res = await fetch(`${API}/admin/wa-billing/share/${this.selectedAccount.id}`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({})
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Share failed', 'error'); return }
        this.showToast('Credit line shared successfully', 'success')
        await this.loadWaBillingDetail()
        // Refresh table badge
        await this.refreshWaBillingMap()
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      } finally {
        this.waShareLoading = false
      }
    },

    async enableCreditMode () {
      this.waEnableLoading = true
      try {
        const res = await fetch(`${API}/admin/wa-billing/enable-credit-mode/${this.selectedAccount.id}`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({ currency: 'USD' })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Enable failed', 'error'); return }
        this.showToast('Credit mode enabled — messages will now deduct from prepaid balance', 'success')
        await this.loadWaBillingDetail()
        await this.refreshWaBillingMap()
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      } finally {
        this.waEnableLoading = false
      }
    },

    async revokeCreditLine () {
      const isManual = this.waBillingDetail.allocation && !this.waBillingDetail.allocation.allocation_config_id
      const msg = isManual
        ? 'Disable credit mode for this user? Their messages will no longer deduct from their prepaid balance.'
        : 'Revoke Meta credit line for this user? Their WhatsApp messaging will be blocked until they set up their own billing.'
      if (!confirm(msg)) return
      this.waRevokeLoading = true
      try {
        const res = await fetch(`${API}/admin/wa-billing/revoke/${this.selectedAccount.id}`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({ reason: 'Admin revoked via dashboard' })
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Revoke failed', 'error'); return }
        this.showToast(isManual ? 'Credit mode disabled' : 'Credit line revoked', 'success')
        await this.loadWaBillingDetail()
        await this.refreshWaBillingMap()
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      } finally {
        this.waRevokeLoading = false
      }
    },

    async submitWaTopup () {
      const isUsd = this.waTopupForm.currency === 'USD'
      if (isUsd ? !this.waTopupForm.amount_usd_direct : !this.waTopupForm.amount_egp_gross) return
      this.waTopupLoading = true
      try {
        let body
        if (isUsd) {
          body = {
            payment_currency: 'USD',
            amount_usd_direct: parseFloat(this.waTopupForm.amount_usd_direct),
            payment_ref: this.waTopupForm.payment_ref || '',
            notes: this.waTopupForm.notes || ''
          }
        } else {
          body = {
            payment_currency: 'EGP',
            amount_egp_gross: parseFloat(this.waTopupForm.amount_egp_gross),
            payment_ref: this.waTopupForm.payment_ref || '',
            notes: this.waTopupForm.notes || ''
          }
        }
        const res = await fetch(`${API}/admin/wa-billing/topup/${this.selectedAccount.id}`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Top-up failed', 'error'); return }
        this.showToast(`Credited $${data.breakdown.usd_credited.toFixed(2)} to account`, 'success')
        this.waShowTopupForm = false
        this.waTopupForm = { currency: 'EGP', amount_egp_gross: '', amount_usd_direct: '', payment_ref: '', notes: '' }
        await this.loadWaBillingDetail()
        await this.refreshWaBillingMap()
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      } finally {
        this.waTopupLoading = false
      }
    },

    async refreshWaBillingMap () {
      try {
        const res = await fetch(`${API}/admin/wa-billing/bulk-status`, { headers: this.apiHeaders() })
        const data = await res.json()
        if (data.success) this.waBillingMap = data.data
      } catch { /* silent */ }
    },

    // ── End WA Billing ───────────────────────────────────────────────────────

    async suspendAccount () {
      const acc = this.selectedAccount
      if (!confirm(`Are you sure you want to suspend/unsuspend "${acc.company_name}"?`)) return
      try {
        const res = await fetch(`${API}/admin/financial/users/${acc.id}/suspend`, {
          method: 'PATCH',
          headers: this.apiHeaders()
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Suspend failed', 'error'); return }
        this.showToast(data.message || 'Account status toggled', 'success')
        await this.loadData()
        const updated = this.accounts.find(a => a.id === acc.id)
        if (updated) this.selectedAccount.status = updated.status
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      }
    },

    async deleteAccount () {
      const acc = this.selectedAccount
      if (!confirm(`This will permanently delete "${acc.company_name}" and all associated data. This cannot be undone. Continue?`)) return
      try {
        const res = await fetch(`${API}/admin/financial/users/${acc.id}`, {
          method: 'DELETE',
          headers: this.apiHeaders()
        })
        const data = await res.json()
        if (!data.success) { this.showToast(data.message || 'Delete failed', 'error'); return }
        this.showToast(`${acc.company_name} deleted`, 'success')
        this.closeDrawer()
        await this.loadData()
      } catch (e) {
        this.showToast('Network error: ' + e.message, 'error')
      }
    },

    // ── File import ──────────────────────────────────
    async importFromFile (evt) {
      const file = evt.target.files[0]
      evt.target.value = ''
      if (!file) return

      this.importMsg = ''
      let rows = []

      try {
        const XLSX = await import('xlsx')
        const data = await file.arrayBuffer()
        const wb = XLSX.read(data, { type: 'array', cellDates: true })
        const ws = wb.Sheets[wb.SheetNames[0]]
        rows = XLSX.utils.sheet_to_json(ws, { defval: '' })
      } catch (e) {
        this.importMsg = 'Could not read file: ' + e.message
        this.importMsgType = 'error'
        return
      }

      if (!rows.length) {
        this.importMsg = 'The file appears to be empty.'
        this.importMsgType = 'error'
        return
      }

      // Flexible column name mapping (case-insensitive)
      const normalize = s => String(s).toLowerCase().replace(/[\s_-]/g, '')
      const COL = {
        name: ['name', 'fullname', 'contactname', 'agentname'],
        company_name: ['company', 'companyname', 'businessname', 'business'],
        phone: ['phone', 'mobile', 'phonenumber', 'tel', 'telephone'],
        email: ['email', 'emailaddress', 'mail'],
        country_code: ['countrycode', 'cc', 'country', 'dialcode'],
        plan_id: ['plan', 'planname', 'subscription', 'planid'],
        from_date: ['startdate', 'fromdate', 'date', 'subscriptiondate', 'start']
      }

      const headers = Object.keys(rows[0])
      const findCol = (aliases) => headers.find(h => aliases.includes(normalize(h)))

      const colName = findCol(COL.name)
      const colCompany = findCol(COL.company_name)
      const colPhone = findCol(COL.phone)
      const colEmail = findCol(COL.email)
      const colCC = findCol(COL.country_code)
      const colPlan = findCol(COL.plan_id)
      const colDate = findCol(COL.from_date)

      if (!colName || !colCompany || !colPhone || !colEmail) {
        this.importMsg = `Required columns not found. Expected: Name, Company, Phone, Email. Found: ${headers.join(', ')}`
        this.importMsgType = 'error'
        return
      }

      const today = new Date().toISOString().split('T')[0]
      const first = this.bulkRows[0]

      const mapped = rows.map(r => {
        // Resolve plan: match by name (case-insensitive)
        let planId = ''
        if (colPlan && r[colPlan]) {
          const pName = normalize(String(r[colPlan]))
          const found = this.plans.find(p => normalize(p.name) === pName)
          planId = found ? found.id : ''
        } else if (first && first.plan_id) {
          planId = first.plan_id
        }

        // Resolve date
        let fromDate = today
        if (colDate && r[colDate]) {
          const d = r[colDate] instanceof Date ? r[colDate] : new Date(r[colDate])
          if (!isNaN(d)) fromDate = d.toISOString().split('T')[0]
        } else if (first && first.from_date) {
          fromDate = first.from_date
        }

        // Country code
        let cc = DEFAULT_COUNTRY_CODE
        if (colCC && r[colCC]) cc = String(r[colCC]).replace(/^\+/, '')
        else if (first && first.country_code) cc = first.country_code

        const row = {
          id: Date.now() + Math.random(),
          name: String(r[colName] || '').trim(),
          company_name: String(r[colCompany] || '').trim(),
          country_code: cc,
          phone: String(r[colPhone] || '').trim(),
          email: String(r[colEmail] || '').trim(),
          plan_id: planId,
          from_date: fromDate,
          password: this.generatePassword(),
          _error: '',
          _phoneError: ''
        }
        row._error = this.validateBulkRow(row)
        return row
      })

      // Must have at least 2 rows
      if (mapped.length < 2) {
        this.importMsg = 'File must contain at least 2 data rows.'
        this.importMsgType = 'error'
        return
      }

      this.bulkRows = mapped
      const errCount = mapped.filter(r => r._error).length
      if (errCount) {
        this.importMsg = `${mapped.length} rows imported — ${errCount} row${errCount !== 1 ? 's' : ''} have validation errors (highlighted in red).`
        this.importMsgType = 'error'
      } else {
        this.importMsg = `${mapped.length} rows imported successfully. Review and submit.`
        this.importMsgType = 'info'
      }
    },

    // ── Bulk create ──────────────────────────────────
    makeBulkRow (inherit = null) {
      const today = new Date().toISOString().split('T')[0]
      return {
        id: Date.now() + Math.random(),
        name: inherit ? inherit.name : '',
        company_name: inherit ? inherit.company_name : '',
        country_code: inherit ? inherit.country_code : DEFAULT_COUNTRY_CODE,
        phone: '',
        email: '',
        plan_id: inherit ? inherit.plan_id : '',
        from_date: inherit ? inherit.from_date : today,
        password: this.generatePassword(),
        _error: '',
        _phoneError: ''
      }
    },

    openBulk () {
      this.drawerOpen = false
      const first = this.makeBulkRow()
      this.bulkRows = [first, this.makeBulkRow(first)]
      this.bulkResults = []
      this.bulkPhase = 'form'
      this.bulkLoading = false
      this.copiedResultIdx = null
      this.importMsg = ''
      this.showBulk = true
    },

    closeBulk () {
      this.showBulk = false
      clearTimeout(this.bulkPhoneCheckTimer)
      setTimeout(() => {
        this.bulkRows = []
        this.bulkResults = []
        this.bulkPhase = 'form'
        this.bulkPhoneCheckState = 'idle'
      }, 300)
    },

    addBulkRow () {
      this.bulkRows.push(this.makeBulkRow(this.bulkRows[0]))
    },

    removeLast () {
      if (this.bulkRows.length <= 2) return
      this.bulkRows.pop()
    },

    removeBulkRow (id) {
      if (this.bulkRows.length <= 2) return
      this.bulkRows = this.bulkRows.filter(r => r.id !== id)
    },

    validateBulkRow (row) {
      if (!row.name.trim()) return 'Name is required'
      if (!row.company_name.trim()) return 'Company is required'
      if (!row.phone.trim()) return 'Phone is required'
      if (!row.email.trim()) return 'Email is required'
      if (!row.plan_id) return 'Plan is required'
      return ''
    },

    revalidateRow (row) {
      row._error = this.validateBulkRow(row)
      if (this.importMsg) this.importMsg = ''
    },

    // Called specifically when phone number or country code changes — clears any API phone error
    onBulkPhoneChange (row) {
      row._phoneError = ''
      this.revalidateRow(row)
    },

    async runBulkPhoneCheck () {
      if (!this.bulkFieldsValid) return
      this.bulkPhoneCheckState = 'checking'
      try {
        const phones = this.bulkRows.map(r => ({ country_code: r.country_code, phone: r.phone.trim() }))
        const res = await fetch(`${API}/admin/financial/check-phones`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({ phones })
        })
        const data = await res.json()
        if (data.success && data.taken.length > 0) {
          data.taken.forEach(key => {
            const dashIdx = key.indexOf('-')
            const cc = key.slice(0, dashIdx)
            const phone = key.slice(dashIdx + 1)
            const row = this.bulkRows.find(r => r.country_code === cc && r.phone.trim() === phone)
            if (row) row._phoneError = 'Phone number already registered'
          })
          this.bulkPhoneCheckState = 'error'
        } else {
          this.bulkPhoneCheckState = 'ok'
        }
      } catch {
        // If the check endpoint fails, allow creation (backend will catch it per-row)
        this.bulkPhoneCheckState = 'ok'
      }
    },

    async submitBulk () {
      // Pre-validation already done reactively; button shouldn't be clickable unless ready.
      // Final guard in case state slipped.
      if (!this.bulkReadyToCreate) return

      this.bulkLoading = true
      const results = []

      for (const row of this.bulkRows) {
        const plan = this.plans.find(p => p.id === row.plan_id)
        try {
          const res = await fetch(`${API}/admin/financial/create-user`, {
            method: 'POST',
            headers: this.apiHeaders(),
            body: JSON.stringify({
              name: row.name,
              company_name: row.company_name,
              country_code: row.country_code,
              phone: row.phone,
              email: row.email,
              password: row.password,
              plan_id: row.plan_id,
              from_date: row.from_date,
              paid: false,
              amount: 0,
              notes: 'Bulk created'
            })
          })
          const data = await res.json()
          if (data.success) {
            results.push({
              name: row.name,
              company_name: row.company_name,
              phone: '+' + row.country_code + row.phone,
              email: row.email,
              plan_name: plan ? plan.name : '—',
              password: row.password,
              status: 'success',
              message: ''
            })
          } else {
            results.push({
              name: row.name,
              company_name: row.company_name,
              phone: '+' + row.country_code + row.phone,
              email: row.email,
              plan_name: plan ? plan.name : '—',
              password: row.password,
              status: 'error',
              message: data.message || 'Failed'
            })
          }
        } catch (e) {
          results.push({
            name: row.name,
            company_name: row.company_name,
            phone: '+' + row.country_code + row.phone,
            email: row.email,
            plan_name: plan ? plan.name : '—',
            password: row.password,
            status: 'error',
            message: 'Network error'
          })
        }
      }

      this.bulkResults = results
      this.bulkLoading = false
      this.bulkPhase = 'results'

      const count = results.filter(r => r.status === 'success').length
      if (count > 0) await this.loadData()
      this.showToast(count + ' of ' + results.length + ' account' + (results.length !== 1 ? 's' : '') + ' created', count === results.length ? 'success' : 'error')
    },

    async copyResultPassword (idx) {
      try {
        await navigator.clipboard.writeText(this.bulkResults[idx].password)
        this.copiedResultIdx = idx
        setTimeout(() => { this.copiedResultIdx = null }, 2000)
      } catch { /* silent */ }
    },

    downloadCredentialsCsv () {
      const rows = this.bulkResults.filter(r => r.status === 'success')
      const header = 'Name,Company,Phone,Email,Plan,Password'
      const lines = rows.map(r => [r.name, r.company_name, r.phone, r.email, r.plan_name, r.password]
        .map(v => '"' + String(v).replace(/"/g, '""') + '"').join(','))
      const csv = [header, ...lines].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'credentials_' + new Date().toISOString().split('T')[0] + '.csv'
      a.click()
      URL.revokeObjectURL(url)
    },

    // ── Credits ─────────────────────────────────────
    async addCredit () {
      if (!this.addCreditAmount || this.addCreditAmount <= 0) return
      this.addCreditLoading = true
      try {
        const token = localStorage.getItem('token')
        const res = await fetch(`${API}/admin/financial/users/${this.selectedAccount.id}/credit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ amount: Number(this.addCreditAmount) })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message || 'Failed to add credit')
        const newBalance = data.balance
        this.selectedAccount.credit = newBalance
        const idx = this.accounts.findIndex(a => a.id === this.selectedAccount.id)
        if (idx !== -1) this.accounts[idx].credit = newBalance
        this.addCreditAmount = ''
        this.showToast(`Credit added. New balance: ${newBalance.toLocaleString()}`, 'success')
      } catch (e) {
        this.showToast(e.message || 'Error adding credit', 'error')
      } finally {
        this.addCreditLoading = false
      }
    },

    openChangePlan (acc) {
      this.changePlanAccount = acc
      this.changePlanId = acc.plan_id || ''
      this.changePlanDate = new Date().toISOString().split('T')[0]
      this.showChangePlan = true
    },

    closeChangePlan () {
      this.showChangePlan = false
      this.changePlanAccount = null
      this.changePlanId = ''
    },

    async submitChangePlan () {
      const plan = this.plans.find(p => p.id === this.changePlanId)
      if (!plan) return
      this.changePlanLoading = true
      try {
        const res = await fetch(`${API}/admin/financial/change-plan`, {
          method: 'POST',
          headers: this.apiHeaders(),
          body: JSON.stringify({
            user_id: this.changePlanAccount.id,
            plan_id: plan.id
          })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message || 'Failed to change plan')
        this.showToast(`Plan updated to ${plan.name}`, 'success')
        this.closeChangePlan()
        await this.loadData()
        // refresh detail drawer if same account is open
        if (this.selectedAccount && this.selectedAccount.id === this.changePlanAccount.id) {
          const updated = this.accounts.find(a => a.id === this.selectedAccount.id)
          if (updated) Object.assign(this.selectedAccount, updated)
        }
      } catch (e) {
        this.showToast(e.message || 'Error changing plan', 'error')
      } finally {
        this.changePlanLoading = false
      }
    },

    openQuickCredit (acc) {
      this.quickCreditAccount = acc
      this.quickCreditAmount = null
      this.showQuickCredit = true
    },

    closeQuickCredit () {
      this.showQuickCredit = false
      this.quickCreditAccount = null
      this.quickCreditAmount = null
    },

    async submitQuickCredit () {
      if (!this.quickCreditAmount || this.quickCreditAmount <= 0) return
      this.quickCreditLoading = true
      try {
        const token = localStorage.getItem('token')
        const res = await fetch(`${API}/admin/financial/users/${this.quickCreditAccount.id}/credit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ amount: Number(this.quickCreditAmount) })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message || 'Failed to add credit')
        const newBalance = data.balance
        // update accounts array
        const acc = this.accounts.find(a => a.id === this.quickCreditAccount.id)
        if (acc) acc.credit = newBalance
        // update detail drawer if same account is open
        if (this.selectedAccount && this.selectedAccount.id === this.quickCreditAccount.id) {
          this.selectedAccount.credit = newBalance
        }
        this.showToast(`Added ${Number(this.quickCreditAmount).toLocaleString()} credits. New balance: ${newBalance.toLocaleString()}`, 'success')
        this.closeQuickCredit()
      } catch (e) {
        this.showToast(e.message || 'Error adding credit', 'error')
      } finally {
        this.quickCreditLoading = false
      }
    },

    // ── Helpers ─────────────────────────────────────
    initials (name) {
      return (name || '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    },

    avatarColor (name) {
      const palette = ['#4f46e5', '#7c3aed', '#db2777', '#dc2626', '#d97706', '#059669', '#0284c7', '#0891b2']
      let h = 0
      for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
      return palette[Math.abs(h) % palette.length]
    },

    formatDate (d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    isExpiringSoon (date) {
      if (!date) return false
      const diff = (new Date(date) - new Date()) / 86400000
      return diff >= 0 && diff <= 7
    },

    isExpired (date) {
      return date && new Date(date) < new Date()
    },

    addDays (dateStr, days) {
      const d = new Date(dateStr)
      d.setDate(d.getDate() + days)
      return d.toISOString().split('T')[0]
    },

    showToast (message, type = 'success') {
      this.toast = { show: true, message, type }
      setTimeout(() => { this.toast.show = false }, 3000)
    }
  },

  mounted () {
    this.newForm.password = this.generatePassword()
    this.loadData()
  }
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────── */
.accts-layout  { display: flex; min-height: 100vh; background: var(--bg-secondary); }
.main-content  { flex: 1; margin-left: 280px; display: flex; flex-direction: column; min-width: 0; overflow: hidden; }
.page-content  { flex: 1; overflow-y: auto; }
.accts-page    { padding: 28px 32px; }

/* ── Page header ────────────────────────────────── */
.page-header      { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.page-header-left { display: flex; align-items: center; gap: 14px; }
.page-icon        { width: 44px; height: 44px; border-radius: 12px; background: #eef2ff; display: flex; align-items: center; justify-content: center; color: #4f46e5; flex-shrink: 0; }
.page-title       { font-size: 22px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; }
.page-sub         { font-size: 13px; color: var(--text-secondary); margin: 0; }

/* ── Page loading / error ───────────────────────── */
.page-loading {
  display: flex; align-items: center; gap: 12px; justify-content: center;
  padding: 60px 0; color: var(--text-secondary); font-size: 14px;
}
.page-error {
  display: flex; align-items: center; gap: 10px; justify-content: center;
  padding: 60px 0; color: #dc2626; font-size: 14px;
}
.btn-retry {
  padding: 5px 14px; border-radius: 7px; border: 1px solid #dc2626;
  background: none; color: #dc2626; font-size: 12px; cursor: pointer;
  margin-left: 8px;
}
.btn-retry:hover { background: #fef2f2; }
.spinner--lg { width: 22px; height: 22px; border-width: 3px; }

/* ── Stat chips ─────────────────────────────────── */
.stat-chips { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
.chip {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 16px; border-radius: 999px;
  font-size: 13px; font-weight: 500; border: 1px solid transparent;
}
.chip-n { font-size: 18px; font-weight: 700; line-height: 1; }
.chip-l { opacity: 0.75; }
.chip--gray   { background: var(--bg-tertiary); color: var(--text-primary); border-color: var(--border-primary); }
.chip--green  { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }
.chip--blue   { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.chip--orange { background: #fff7ed; color: #c2410c; border-color: #fed7aa; }
.chip--red    { background: #fef2f2; color: #dc2626; border-color: #fecaca; }

/* ── Toolbar ────────────────────────────────────── */
.toolbar {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 16px; flex-wrap: wrap;
}
.search-box {
  display: flex; align-items: center; gap: 8px;
  background: var(--bg-card); border: 1px solid var(--border-primary); border-radius: 8px;
  padding: 0 12px; flex: 1; min-width: 220px; max-width: 400px;
  color: var(--text-secondary);
}
.search-box input {
  border: none; outline: none; background: transparent;
  font-size: 13px; color: var(--text-primary); padding: 9px 0; flex: 1;
}
.filter-select {
  padding: 8px 12px; border: 1px solid var(--border-primary); border-radius: 8px;
  font-size: 13px; background: var(--bg-card); color: var(--text-primary);
  cursor: pointer; outline: none;
}
.result-count { font-size: 13px; color: var(--text-secondary); margin-left: auto; white-space: nowrap; }

/* ── Table ──────────────────────────────────────── */
.table-card { background: var(--bg-card); border-radius: 12px; border: 1px solid var(--border-primary); overflow: hidden; }
.accts-table { width: 100%; border-collapse: collapse; }
.accts-table thead th {
  padding: 11px 16px; font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .05em; color: var(--text-secondary); background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-primary); text-align: left;
}
.accts-table tbody td { padding: 14px 16px; font-size: 13px; border-bottom: 1px solid var(--border-primary); vertical-align: middle; }
.acct-row { cursor: pointer; transition: background .12s; }
.acct-row:hover { background: var(--bg-secondary); }
.acct-row:last-child td { border-bottom: none; }

.acct-cell   { display: flex; align-items: center; gap: 12px; }
.avatar {
  width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center;
  justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0;
}
.acct-name    { font-weight: 600; color: var(--text-primary); }
.acct-company { font-size: 12px; color: var(--text-secondary); margin-top: 1px; }
.acct-email   { font-size: 11px; color: var(--text-tertiary); margin-top: 1px; }

.plan-badge {
  font-size: 11px; font-weight: 600; padding: 3px 9px;
  border-radius: 6px; background: #eef2ff; color: #4f46e5; white-space: nowrap;
}

.status-badge { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.status-active  { background: #f0fdf4; color: #15803d; }
.status-pending { background: #eff6ff; color: #1d4ed8; }
.status-expired { background: #fef2f2; color: #dc2626; }

.date-cell  { font-size: 12px; color: var(--text-secondary); white-space: nowrap; }
.text-orange { color: #c2410c; font-weight: 600; }
.text-red    { color: #dc2626; font-weight: 600; }
.text-muted  { color: var(--text-tertiary); }

.btn-view {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 12px; border-radius: 7px; border: 1px solid var(--border-primary);
  background: var(--bg-card); font-size: 12px; font-weight: 500; color: var(--text-primary);
  cursor: pointer; transition: all .15s;
}
.btn-view:hover { border-color: #4f46e5; color: #4f46e5; background: #eef2ff; }

.empty-row { text-align: center; color: var(--text-tertiary); font-size: 13px; padding: 40px 16px; }

/* ── Drawer overlay ─────────────────────────────── */
.drawer-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  z-index: 900; backdrop-filter: blur(2px);
}

/* ── Drawer panel ───────────────────────────────── */
.drawer {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: 460px; background: var(--bg-card); z-index: 910;
  display: flex; flex-direction: column;
  box-shadow: -8px 0 40px rgba(0,0,0,.14);
}
.drawer--wide { width: 520px; }

.drawer-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
}
.drawer-header-info { display: flex; align-items: center; gap: 12px; }
.drawer-avatar {
  width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center;
  justify-content: center; font-size: 15px; font-weight: 700; color: #fff; flex-shrink: 0;
}
.drawer-title { font-size: 17px; font-weight: 700; margin: 0; color: var(--text-primary); }
.drawer-sub   { font-size: 12px; color: var(--text-secondary); margin: 2px 0 0; }
.drawer-close {
  font-size: 22px; font-weight: 300; color: var(--text-tertiary); background: none;
  border: none; cursor: pointer; line-height: 1; padding: 4px; border-radius: 6px;
  transition: all .15s;
}
.drawer-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }

.drawer-body   { flex: 1; overflow-y: auto; padding: 20px 24px; }
.drawer-footer {
  display: flex; gap: 10px; padding: 16px 24px;
  border-top: 1px solid var(--border-primary); flex-shrink: 0;
}

/* ── Status bar (detail) ────────────────────────── */
.detail-status-bar {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 24px; background: var(--bg-secondary); border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
  flex-wrap: wrap;
}
.detail-plan    { font-size: 12px; font-weight: 600; color: #4f46e5; }
.detail-expires { font-size: 12px; color: var(--text-secondary); }

/* ── Detail tabs ────────────────────────────────── */
.detail-tabs {
  display: flex; gap: 0; border-bottom: 1px solid var(--border-primary); flex-shrink: 0; background: var(--bg-card);
}
.detail-tab {
  padding: 11px 20px; font-size: 13px; font-weight: 500; color: var(--text-secondary);
  background: none; border: none; border-bottom: 2px solid transparent;
  cursor: pointer; transition: all .15s; white-space: nowrap;
}
.detail-tab:hover            { color: #4f46e5; }
.detail-tab--active          { color: #4f46e5; border-bottom-color: #4f46e5; font-weight: 600; }

/* ── Form elements ──────────────────────────────── */
.form-group     { margin-bottom: 16px; }
.form-group-sm  { flex: 1; }
.form-row-2     { display: flex; gap: 12px; }
.mt-10  { margin-top: 10px; }
.mt-16  { margin-top: 16px; }

.form-group label,
.form-group-sm label {
  display: block; font-size: 12px; font-weight: 600;
  color: var(--text-primary); margin-bottom: 5px;
}
.req { color: #ef4444; }

.form-group input,
.form-group select,
.form-group textarea,
.form-group-sm input,
.form-group-sm select {
  width: 100%; padding: 8px 12px; border: 1px solid var(--border-primary); border-radius: 8px;
  font-size: 13px; color: var(--text-primary); background: var(--bg-card); box-sizing: border-box;
  transition: border-color .15s; outline: none;
}
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus,
.form-group-sm input:focus,
.form-group-sm select:focus {
  border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.1);
}

.full-select { width: 100%; padding: 8px 12px; border: 1px solid var(--border-primary); border-radius: 8px; font-size: 13px; background: var(--bg-card); color: var(--text-primary); outline: none; }

.field-hint        { font-size: 11px; color: var(--text-tertiary); margin: 4px 0 0; }
.field-hint--warn  { color: #d97706; }
.field-hint--error { color: #dc2626; font-weight: 500; }

.phone-row        { display: flex; gap: 8px; min-width: 0; }
.country-select   { width: 72px; max-width: 72px; flex-shrink: 0; padding: 8px 4px; border: 1px solid var(--border-primary); border-radius: 8px; font-size: 13px; background: var(--bg-card); color: var(--text-primary); outline: none; box-sizing: border-box; }
.phone-row input  { flex: 1; min-width: 0; padding: 8px 12px; border: 1px solid var(--border-primary); border-radius: 8px; font-size: 13px; color: var(--text-primary); background: var(--bg-card); outline: none; }
.phone-code-static {
  display: flex; align-items: center; padding: 0 12px;
  background: var(--bg-tertiary); border: 1px solid var(--border-primary); border-radius: 8px 0 0 8px;
  font-size: 13px; color: var(--text-secondary); white-space: nowrap; border-right: none;
}
.phone-row input:last-child { border-radius: 0 8px 8px 0; }

.password-row   { display: flex; gap: 6px; align-items: center; }
.password-input { flex: 1; font-family: monospace; font-size: 12.5px !important; letter-spacing: .04em; background: var(--bg-secondary) !important; color: var(--text-primary) !important; }

.btn-icon {
  width: 34px; height: 34px; border-radius: 8px; border: 1px solid var(--border-primary);
  background: var(--bg-card); display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-secondary); transition: all .15s; flex-shrink: 0;
}
.btn-icon:hover      { border-color: #4f46e5; color: #4f46e5; background: #eef2ff; }
.btn-icon--done      { border-color: #16a34a; color: #16a34a; background: #f0fdf4; }

/* ── Plan toggle ────────────────────────────────── */
.plan-mode-toggle { display: flex; gap: 6px; margin-bottom: 10px; }
.toggle-btn {
  padding: 5px 14px; border-radius: 7px; border: 1px solid var(--border-primary);
  font-size: 12px; font-weight: 500; cursor: pointer; background: var(--bg-secondary); color: var(--text-secondary);
  transition: all .15s;
}
.toggle-btn--on         { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.toggle-btn--enterprise { background: #f59e0b; color: #fff; border-color: #f59e0b; }

.enterprise-box { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 14px; margin-top: 6px; display: flex; flex-direction: column; gap: 12px; }

/* ── Payment box ────────────────────────────────── */
.payment-box {
  background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 10px; padding: 14px; margin-bottom: 16px;
  transition: opacity .15s;
}
.payment-box--disabled { opacity: .5; }
.checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; color: var(--text-primary); cursor: pointer; }
.checkbox-label input:disabled + span { cursor: not-allowed; }
.input-locked { background: var(--bg-tertiary) !important; color: var(--text-secondary) !important; cursor: not-allowed; }
.checkbox-label input[type="checkbox"] { width: 15px; height: 15px; cursor: pointer; }

/* ── Time row ───────────────────────────────────── */
.time-row   { display: flex; align-items: center; gap: 10px; }
.time-sep   { font-size: 12px; color: var(--text-tertiary); }
.time-row input { flex: 1; padding: 8px 12px; border: 1px solid var(--border-primary); border-radius: 8px; font-size: 13px; color: var(--text-primary); background: var(--bg-card); outline: none; }

/* ── Days row ───────────────────────────────────── */
.days-row  { display: flex; flex-wrap: wrap; gap: 8px; }
.day-check {
  display: flex; align-items: center; gap: 5px;
  padding: 5px 10px; border: 1px solid var(--border-primary); border-radius: 7px;
  font-size: 12px; font-weight: 500; cursor: pointer; color: var(--text-primary);
  transition: all .15s; user-select: none;
}
.day-check:has(input:checked) { background: #eef2ff; border-color: #4f46e5; color: #4f46e5; }
.day-check input { display: none; }

/* ── Payments tab ───────────────────────────────── */
.payments-header   { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.tab-section-title { font-size: 14px; font-weight: 700; color: var(--text-primary); margin: 0 0 14px; }
.empty-state       { text-align: center; color: var(--text-tertiary); font-size: 13px; padding: 32px 0; display: flex; flex-direction: column; align-items: center; gap: 8px; }
.payment-list      { display: flex; flex-direction: column; gap: 8px; }
.payment-item {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 14px; border: 1px solid var(--border-primary); border-radius: 10px; background: var(--bg-secondary);
}
.payment-ref  { font-size: 12px; font-family: monospace; color: var(--text-primary); font-weight: 600; }
.payment-meta { font-size: 11px; color: var(--text-tertiary); margin-top: 2px; }
.payment-amount { font-size: 14px; font-weight: 700; color: #059669; text-align: right; }
.payment-date   { font-size: 11px; color: var(--text-tertiary); text-align: right; margin-top: 2px; }

.add-payment-form {
  background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 10px;
  padding: 16px; margin-bottom: 16px; display: flex; flex-direction: column; gap: 12px;
}
.add-payment-form h4 { font-size: 13px; font-weight: 700; color: #0284c7; margin: 0 0 4px; }

/* ── Access tab ─────────────────────────────────── */
.access-section         { margin-bottom: 24px; }
.access-section--danger { border-top: 1px solid #fecaca; padding-top: 20px; }
.danger-label           { color: #dc2626 !important; }
.danger-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 0; border-bottom: 1px solid #fee2e2;
}
.danger-row:last-child         { border-bottom: none; }
.danger-action-title           { font-size: 13px; font-weight: 600; color: var(--text-primary); }
.danger-action-sub             { font-size: 12px; color: var(--text-tertiary); margin-top: 2px; }

/* ── Buttons ────────────────────────────────────── */
.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; background: #4f46e5; color: #fff;
  border: none; border-radius: 9px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background .15s;
}
.btn-primary:hover { background: #4338ca; }

.btn-submit {
  flex: 1; padding: 10px; background: #4f46e5; color: #fff;
  border: none; border-radius: 9px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background .15s; display: flex; align-items: center; justify-content: center; gap: 6px;
}
.btn-submit:hover    { background: #4338ca; }
.btn-submit:disabled { opacity: .6; cursor: not-allowed; }
.btn-submit.btn-full { width: 100%; flex: none; }

.btn-cancel {
  flex: 1; padding: 10px; background: var(--bg-card); color: var(--text-primary);
  border: 1px solid var(--border-primary); border-radius: 9px; font-size: 13px; font-weight: 500;
  cursor: pointer; transition: background .15s;
}
.btn-cancel:hover { background: var(--bg-secondary); }

.btn-secondary-sm {
  padding: 6px 12px; background: var(--bg-card); border: 1px solid #4f46e5; color: #4f46e5;
  border-radius: 7px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s;
}
.btn-secondary-sm:hover { background: #eef2ff; }

.btn-danger-outline {
  padding: 7px 14px; background: var(--bg-card); border: 1px solid #dc2626; color: #dc2626;
  border-radius: 7px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s; white-space: nowrap;
}
.btn-danger-outline:hover { background: #fef2f2; }

.btn-danger {
  padding: 7px 14px; background: #dc2626; border: none; color: #fff;
  border-radius: 7px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s; white-space: nowrap;
}
.btn-danger:hover { background: #b91c1c; }

.form-error { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; border-radius: 8px; padding: 10px 12px; font-size: 13px; margin-top: 4px; }

/* ── Spinner ────────────────────────────────────── */
.spinner {
  width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff; border-radius: 50%;
  animation: spin .7s linear infinite; flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Toast ──────────────────────────────────────── */
.toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 1100;
  padding: 12px 20px; border-radius: 10px; font-size: 13px; font-weight: 500;
  box-shadow: 0 4px 20px rgba(0,0,0,.15); max-width: 320px;
}
.toast--success { background: #065f46; color: #fff; }
.toast--error   { background: #dc2626; color: #fff; }
.toast--info    { background: #1e40af; color: #fff; }

/* ── Transitions ────────────────────────────────── */
.drawer-enter-active, .drawer-leave-active { transition: transform .28s cubic-bezier(.4,0,.2,1); }
.drawer-enter-from, .drawer-leave-to       { transform: translateX(100%); }

.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to       { opacity: 0; }

.toast-enter-active, .toast-leave-active { transition: all .3s ease; }
.toast-enter-from, .toast-leave-to       { opacity: 0; transform: translateY(12px); }

/* ── Header actions row ──────────────────────────── */
.header-actions { display: flex; gap: 10px; align-items: center; }

.btn-secondary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; background: var(--bg-card); color: var(--text-primary);
  border: 1px solid var(--border-primary); border-radius: 9px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .15s;
}
.btn-secondary:hover { border-color: #4f46e5; color: #4f46e5; background: #eef2ff; }

/* ── Bulk drawer width ───────────────────────────── */
.drawer--bulk { width: 860px; }
@media (max-width: 920px) { .drawer--bulk { width: 98vw; } }

/* ── Bulk icon chip ──────────────────────────────── */
.bulk-icon {
  width: 38px; height: 38px; border-radius: 10px; background: #eef2ff;
  display: flex; align-items: center; justify-content: center; color: #4f46e5; flex-shrink: 0;
}

/* ── Bulk toolbar ────────────────────────────────── */
.bulk-toolbar {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 24px; border-bottom: 1px solid var(--border-primary); flex-shrink: 0; flex-wrap: wrap;
}
.btn-import {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 8px; border: 1px solid var(--border-primary);
  background: var(--bg-card); font-size: 12px; font-weight: 500; color: var(--text-secondary);
  cursor: pointer; transition: all .15s; margin-left: auto;
}
.btn-import:hover { border-color: #4f46e5; color: #4f46e5; background: #eef2ff; }

.import-msg {
  margin: 0 24px 0; padding: 8px 12px; border-radius: 8px;
  font-size: 12px; line-height: 1.5;
}
.import-msg--info  { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
.import-msg--error { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }

/* ── Bulk validation status bar ─────────────────── */
.bulk-status-bar {
  padding: 10px 24px; border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary); flex-shrink: 0;
}
.bulk-status {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 500;
}
.bulk-status--idle    { color: var(--text-tertiary); }
.bulk-status--checking { color: #2563eb; }
.bulk-status--ok      { color: #059669; }
.bulk-status--error   { color: #dc2626; }
.spinner--dark {
  width: 12px; height: 12px; border: 2px solid rgba(37,99,235,.25);
  border-top-color: #2563eb; border-radius: 50%;
  animation: spin .7s linear infinite; flex-shrink: 0;
}

/* ── Import field reference strip ────────────────── */
.import-fields {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  padding: 8px 24px; background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-primary); font-size: 11px;
}
.import-fields-label { font-weight: 600; color: var(--text-secondary); margin-right: 2px; white-space: nowrap; }
.import-field {
  padding: 2px 8px; border-radius: 5px; font-weight: 500;
  background: var(--bg-card); border: 1px solid var(--border-primary); color: var(--text-secondary);
  white-space: nowrap;
}
.import-field--required {
  border-color: #fca5a5; background: #fef2f2; color: #b91c1c;
}
.import-req-dot {
  display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: #ef4444; padding: 0; border: none; vertical-align: middle;
}
.import-fields-note { font-size: 10px; color: var(--text-tertiary); margin-left: 4px; display: flex; align-items: center; gap: 4px; }

/* +/- row counter */
.row-counter {
  display: inline-flex; align-items: center; gap: 0;
  border: 1px solid var(--border-primary); border-radius: 8px; overflow: hidden;
}
.btn-counter {
  width: 32px; height: 32px; border: none; background: var(--bg-card);
  font-size: 18px; font-weight: 500; color: #4f46e5;
  cursor: pointer; transition: background .12s; line-height: 1;
  display: flex; align-items: center; justify-content: center;
}
.btn-counter:hover:not(:disabled) { background: #eef2ff; }
.btn-counter:disabled { color: var(--text-tertiary); cursor: not-allowed; }
.counter-val {
  padding: 0 10px; font-size: 12px; font-weight: 600;
  color: var(--text-primary); border-left: 1px solid var(--border-primary); border-right: 1px solid var(--border-primary);
  line-height: 32px; white-space: nowrap;
}

/* ── Bulk scrollable table area ──────────────────── */
.bulk-scroll { flex: 1; overflow: auto; padding: 0 24px 16px; }

.bulk-table { width: 100%; border-collapse: collapse; min-width: 720px; }

.bulk-table thead th {
  position: sticky; top: 0; z-index: 1;
  padding: 10px 6px; font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .05em; color: var(--text-secondary); background: var(--bg-card);
  border-bottom: 1px solid var(--border-primary); text-align: left; white-space: nowrap;
}

.bulk-table tbody td { padding: 5px 4px; vertical-align: middle; }

/* column widths */
.col-num     { width: 30px; }
.col-name    { width: 140px; }
.col-company { width: 140px; }
.col-phone   { width: 160px; }
.col-email   { width: 180px; }
.col-plan    { width: 130px; }
.col-date    { width: 120px; }
.col-remove  { width: 36px; }

.bulk-row { transition: background .1s; }
.bulk-row--error td { border-top: 1px solid #fca5a5; border-bottom: 1px solid #fca5a5; background: #fff5f5; }

.row-num { font-size: 11px; color: var(--text-tertiary); font-weight: 600; display: block; text-align: center; }

/* ── Compact inputs ──────────────────────────────── */
.bulk-input {
  width: 100%; padding: 6px 8px; border: 1px solid var(--border-primary); border-radius: 6px;
  font-size: 12px; color: var(--text-primary); background: var(--bg-card);
  box-sizing: border-box; outline: none; transition: border-color .15s;
}
.bulk-input:focus { border-color: #4f46e5; box-shadow: 0 0 0 2px rgba(79,70,229,.1); }

.bulk-select {
  width: 100%; padding: 6px 8px; border: 1px solid var(--border-primary); border-radius: 6px;
  font-size: 12px; color: var(--text-primary); background: var(--bg-card);
  box-sizing: border-box; outline: none; cursor: pointer;
}
.bulk-select:focus { border-color: #4f46e5; }

.bulk-phone-row { display: flex; gap: 4px; }
.bulk-cc { width: 58px; flex-shrink: 0; padding: 6px 4px; border: 1px solid var(--border-primary); border-radius: 6px; font-size: 11px; color: var(--text-primary); background: var(--bg-card); outline: none; }
.bulk-phone-num { flex: 1; }

.btn-remove-row {
  width: 28px; height: 28px; border-radius: 6px; border: 1px solid var(--border-primary);
  background: transparent; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-secondary); transition: all .15s;
}
.btn-remove-row:hover:not(:disabled) { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
.btn-remove-row:disabled { opacity: .35; cursor: not-allowed; }

/* ── Per-row error messages ──────────────────────── */
.bulk-row-error {
  font-size: 11px; color: #dc2626; padding: 2px 6px 6px;
}

/* ── Results list ────────────────────────────────── */
.bulk-results-list { display: flex; flex-direction: column; gap: 8px; padding-top: 16px; }

.result-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px; border-radius: 10px; border: 1px solid var(--border-primary);
  background: var(--bg-card);
}
.result-item--success { border-left: 3px solid #16a34a; }
.result-item--error   { border-left: 3px solid #dc2626; }

.result-avatar {
  width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center;
  justify-content: center; font-size: 12px; font-weight: 700; color: #fff; flex-shrink: 0;
}
.result-info { flex: 1; min-width: 0; }
.result-name    { font-size: 13px; font-weight: 600; color: var(--text-primary); }
.result-company { font-size: 11px; color: var(--text-secondary); margin-top: 1px; }

.result-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 600;
}
.result-badge--ok  { background: #f0fdf4; color: #15803d; }
.result-badge--err { background: #fef2f2; color: #dc2626; }

.result-plan { color: #4f46e5; font-weight: 500; }

/* ── Password cell in results ────────────────────── */
.result-pw-cell {
  display: flex; align-items: center; gap: 6px;
  background: var(--bg-secondary); border: 1px solid var(--border-primary);
  border-radius: 7px; padding: 4px 8px; margin-right: 8px;
}
.result-pw-dots {
  font-family: monospace; font-size: 11px; color: var(--text-secondary);
  letter-spacing: .04em; white-space: nowrap; user-select: all;
}
.btn-copy-pw {
  width: 24px; height: 24px; border-radius: 5px; border: none;
  background: transparent; display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-secondary); transition: all .15s; flex-shrink: 0;
}
.btn-copy-pw:hover { background: var(--bg-tertiary); color: #4f46e5; }
.btn-copy-pw--done { color: #16a34a; }

/* ── Download CSV button ─────────────────────────── */
.btn-download {
  display: inline-flex; align-items: center; gap: 6px;
}

/* ── Credit badge (main table) ───────────────────── */
.credit-cell { white-space: nowrap; }

.btn-credit-quick {
  display: inline-flex; align-items: center; gap: 4px;
  margin-left: 7px; padding: 4px 11px; border-radius: 7px;
  border: 1px solid #6ee7b7; background: #d1fae5; color: #065f46;
  font-size: 12px; font-weight: 700; line-height: 1; vertical-align: middle;
  cursor: pointer; transition: all .18s;
}
.btn-credit-quick:hover { background: #059669; color: #fff; border-color: #059669; }
.credit-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 600;
}
.credit-badge--has  { background: #ecfdf5; color: #059669; }
.credit-badge--zero { background: var(--bg-tertiary); color: var(--text-tertiary); }

/* ── WA Billing column + badge ─────────────────────── */
.wa-billing-cell { white-space: nowrap; }
.wa-text-muted { font-size: 12px; color: var(--text-tertiary); }
.wa-badge {
  display: inline-block; padding: 2px 8px; border-radius: 10px;
  font-size: 11px; font-weight: 600; letter-spacing: .03em;
}
.wa-badge--active  { background: rgba(34,197,94,.12); color: rgba(34,197,94,.9); }
.wa-badge--revoked { background: rgba(239,68,68,.12); color: rgba(239,68,68,.9); }
.wa-badge--pending { background: rgba(234,179,8,.12); color: rgba(202,138,4,.9); }
.wa-badge--failed  { background: rgba(239,68,68,.12); color: rgba(239,68,68,.9); }
.wa-badge--none    { background: var(--bg-tertiary); color: var(--text-tertiary); }
.wa-balance { font-size: 11px; color: rgba(5, 150, 105, .85); font-weight: 600; margin-top: 2px; }

/* ── WA Billing tab ─────────────────────────────────── */
.wa-settings-panel { margin-bottom: 14px; }
.wa-settings-body { border-top: 1px solid var(--border-color); padding-top: 14px; margin-top: 10px; }
.wa-load-prompt { text-align: center; padding: 32px 0; }
.wa-section-card {
  background: var(--bg-secondary); border: 1px solid var(--border-color);
  border-radius: 10px; padding: 16px; margin-bottom: 14px;
}
.wa-section-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
}
.wa-notice { font-size: 13px; color: var(--text-secondary); padding: 8px 0; }
.wa-meta-row { display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0; border-bottom: 1px solid var(--border-color); }
.wa-meta-row:last-of-type { border-bottom: none; }
.wa-meta-label { color: var(--text-secondary); }
.wa-meta-val   { font-weight: 500; color: var(--text-primary); font-family: monospace; font-size: 11px; }
.wa-action-row { margin-top: 14px; display: flex; gap: 8px; }

.btn-icon-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s, color .15s;
}
.btn-icon-sm:hover:not(:disabled) { background: var(--bg-tertiary); color: var(--text-primary); }
.btn-icon-sm:disabled { opacity: .45; cursor: not-allowed; }

.wa-verify-result {
  margin-top: 8px;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wa-verify-result--ok  { background: rgba(34, 197, 94, .08); border: 1px solid rgba(34, 197, 94, .25); }
.wa-verify-result--err { background: rgba(239, 68, 68, .08);  border: 1px solid rgba(239, 68, 68, .25); }
.wa-verify-row { display: flex; align-items: center; gap: 6px; }
.wa-verify-label { color: var(--text-secondary); min-width: 60px; }
.wa-verify-val   { font-weight: 500; color: var(--text-primary); }

.wa-balance-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 12px;
}
.wa-balance-item { background: var(--bg-primary); border-radius: 8px; padding: 10px; text-align: center; }
.wa-balance-item--highlight { background: rgba(34, 197, 94, .08); }
.wa-bal-label { font-size: 10px; text-transform: uppercase; letter-spacing: .05em; color: var(--text-tertiary); margin-bottom: 4px; }
.wa-bal-val   { font-size: 14px; font-weight: 700; color: var(--text-primary); }

.wa-topup-form { border-top: 1px solid var(--border-color); margin-top: 12px; padding-top: 14px; }

.wa-currency-toggle { display: flex; gap: 6px; margin-bottom: 14px; }
.wa-cur-btn {
  flex: 1; padding: 7px 0; border-radius: 8px; border: 1px solid var(--border-color);
  background: var(--bg-secondary); color: var(--text-secondary);
  font-size: 12px; font-weight: 500; cursor: pointer; transition: all .15s;
}
.wa-cur-btn--active { background: #4f46e5; color: #fff; border-color: #4f46e5; }

.wa-topup-cur-badge {
  display: inline-block; font-size: 9px; font-weight: 700; padding: 1px 5px;
  border-radius: 4px; margin-right: 4px; vertical-align: middle; letter-spacing: .04em;
}
.wa-topup-cur-badge--egp { background: rgba(79,70,229,.12); color: #4f46e5; }
.wa-topup-cur-badge--usd { background: rgba(34,197,94,.12); color: #15803d; }
.wa-breakdown-box {
  background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 8px;
  padding: 10px 14px; font-size: 13px; margin-top: 12px;
}
.wa-bk-row { display: flex; justify-content: space-between; padding: 4px 0; }
.wa-bk-row--deduct span:last-child { color: #b91c1c; }
.wa-bk-row--divider { border-top: 1px solid var(--border-color); margin: 6px 0; }
.wa-bk-row--result  { font-weight: 700; color: #059669; font-size: 14px; }

.wa-topup-list { display: flex; flex-direction: column; gap: 8px; }
.wa-topup-item { background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 12px; }
.wa-topup-main { display: flex; justify-content: space-between; align-items: center; }
.wa-topup-amounts { display: flex; align-items: center; gap: 8px; }
.wa-topup-egp  { font-weight: 600; font-size: 13px; color: var(--text-primary); }
.wa-topup-usd  { font-size: 12px; color: rgba(5, 150, 105, .85); }
.wa-topup-remaining { font-size: 12px; font-weight: 700; color: rgba(99, 102, 241, .9); }
.wa-topup-remaining--zero { color: var(--text-tertiary); }
.wa-topup-meta { display: flex; gap: 10px; font-size: 11px; color: var(--text-secondary); margin-top: 4px; }
.wa-topup-ref  { font-family: monospace; background: var(--bg-tertiary); padding: 0 4px; border-radius: 3px; }
.wa-topup-notes { font-size: 11px; color: var(--text-tertiary); margin-top: 4px; font-style: italic; }

/* ── Credits tab in detail drawer ────────────────── */
.credit-balance-box {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-radius: 12px; padding: 20px; text-align: center; margin-bottom: 20px;
}
.credit-balance-label { font-size: 12px; font-weight: 500; color: rgba(255,255,255,.75); letter-spacing: .05em; text-transform: uppercase; }
.credit-balance-val   { font-size: 36px; font-weight: 800; color: #fff; line-height: 1.1; margin: 4px 0 2px; }
.credit-unit          { font-size: 13px; color: rgba(255,255,255,.65); }

.credit-presets { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.credit-preset-btn {
  padding: 5px 12px; border-radius: 8px; border: 1px solid var(--border-primary);
  background: var(--bg-secondary); color: var(--text-primary); font-size: 12px; font-weight: 500;
  cursor: pointer; transition: all .15s;
}
.credit-preset-btn:hover         { border-color: #4f46e5; color: #4f46e5; background: #eef2ff; }
.credit-preset-btn--active       { background: #4f46e5; color: #fff; border-color: #4f46e5; }

.credit-add-row { display: flex; gap: 8px; align-items: center; }
.credit-add-row input { flex: 1; padding: 8px 12px; border: 1px solid var(--border-primary); border-radius: 8px; font-size: 13px; color: var(--text-primary); background: var(--bg-card); outline: none; }
.credit-add-row input:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }

/* ── Modals ──────────────────────────────────────── */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  z-index: 950; display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(3px);
}
.credit-modal {
  background: var(--bg-card); border-radius: 16px;
  width: 540px; max-width: 96vw; max-height: 88vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}
.credit-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid var(--border-primary); flex-shrink: 0;
}
.credit-modal-header h3 { font-size: 16px; font-weight: 700; color: var(--text-primary); margin: 0; }
.credit-modal-body { flex: 1; overflow-y: auto; padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }

.credit-modal-footer {
  display: flex; gap: 10px; padding: 16px 24px;
  border-top: 1px solid var(--border-primary); flex-shrink: 0; align-items: center;
}
/* ── Plan cell in table ──────────────────────────── */
.plan-cell { white-space: nowrap; }
.btn-plan-change {
  display: inline-flex; align-items: center;
  margin-left: 7px; padding: 3px 9px; border-radius: 6px;
  border: 1px solid #c7d2fe; background: #eef2ff; color: #4338ca;
  font-size: 11px; font-weight: 600; line-height: 1; vertical-align: middle;
  cursor: pointer; transition: all .18s;
}
.btn-plan-change:hover { background: #4f46e5; color: #fff; border-color: #4f46e5; }

.plan-change-summary {
  font-size: 12px; color: var(--text-secondary);
  background: var(--bg-secondary); border: 1px solid var(--border-primary);
  border-radius: 8px; padding: 10px 12px;
}

/* ── Quick credit modal (narrower) ──────────────── */
.credit-modal--quick { width: 420px; }
.credit-custom-input {
  width: 100%; padding: 9px 12px; border: 1px solid var(--border-primary); border-radius: 8px;
  font-size: 13px; color: var(--text-primary); background: var(--bg-card); outline: none; box-sizing: border-box;
}
.credit-custom-input:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.1); }
</style>
